import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Eye, EyeOff, Save, Check, AlertCircle, FileText, 
  User, Calendar, Shield, Baby, Clock,
  CheckCircle, XCircle, UserCheck,
  Globe, MapPin, GraduationCap, Award, BookOpen, Star, Building, CreditCard, Tag,
  ZoomIn, ZoomOut, RefreshCw, RotateCw, AlertTriangle, Download,
  Plus, Trash2, Infinity as InfinityIcon
} from 'lucide-react';
import api, { staffAPI, confidentialAPI } from '../services/api';
import toast from '../utils/toast';
import Modal from './UI/Modal';
import ConfirmModal from './UI/ConfirmModal';
import { InfoBadge } from './UI/InfoBadge';
import { StatusBadge } from './UI/StatusBadge';
import { BcaSchemeBadge } from './UI/BcaSchemeBadge';
import {
  detectConflicts as detectConflictsUtil,
  filterConflictsToVisibleFields,
  normalizeIdCardIdentifiers,
  normalizeNationality,
} from '../utils/ocrConflictUtils';
import { formatDate, parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import { isHeicFile, convertHeicBlobToJpeg, getPreviewDocumentType, inferContentType } from '../utils/imageUtils';
import { hasMeaningfulDocumentOcrData } from '../utils/documentOcrUtils';
import { useIsMobile } from '../hooks/useIsMobile';
import { useImageViewer } from '../hooks/useImageViewer';
import { useSafeDiscardAndClose } from '../hooks/useSafeDiscardAndClose';
import { useSingaporeBanks } from '../hooks/useConfigData';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import ExtractAgainButton from './UI/ExtractAgainButton';
import PdfViewer from './UI/PdfViewer';
import FormSelect from './UI/FormSelect';
import DatalistCombobox from './UI/DatalistCombobox';
import DatePickerField from './UI/DatePickerField';
import { RACE_OPTIONS } from '../constants/raceOptions';

// Singapore public + private maternity hospitals for the interim birth notification combobox
const SINGAPORE_HOSPITALS = [
  // Public hospitals
  "KK Women's and Children's Hospital",
  'National University Hospital',
  'Singapore General Hospital',
  'Tan Tock Seng Hospital',
  'Ng Teng Fong General Hospital',
  'Khoo Teck Puat Hospital',
  'Changi General Hospital',
  'Sengkang General Hospital',
  // Private hospitals
  'Mount Elizabeth Hospital',
  'Mount Elizabeth Novena Hospital',
  'Parkway East Hospital',
  'Gleneagles Hospital',
  'Thomson Medical Centre',
  'Farrer Park Hospital',
  'Mount Alvernia Hospital',
  'Raffles Hospital',
];

// Source document type options matching backend INTERIM_SOURCE_DOCUMENT_TYPES
const INTERIM_SOURCE_DOC_OPTIONS = [
  { value: 'birth_notification',       label: 'Birth Notification / Notification of Live Birth' },
  { value: 'hospital_discharge_letter', label: 'Hospital Maternity Discharge Letter' },
  { value: 'hospital_admission_summary', label: 'Hospital Admission / Discharge Summary' },
  { value: 'doctor_memo',              label: 'Doctor Memo Confirming Delivery' },
  { value: 'hospital_billing_record',  label: 'Hospital Billing Record With Delivery Details' },
  { value: 'mc_maternity_leave',       label: 'Medical Certificate Stating Maternity Leave' },
  { value: 'other',                    label: 'Other' },
];

// Fallback Singapore bank list when catalog API is unavailable.
// Values follow the MAS Financial Institutions Directory legal-entity names.
// POSB is intentionally absent — brand of DBS Bank Ltd; keyword matcher routes to DBS.
const FALLBACK_SINGAPORE_BANKS = [
  'DBS BANK LTD',
  'OVERSEA-CHINESE BANKING CORP LTD',
  'UNITED OVERSEAS BANK LIMITED',
  'STANDARD CHARTERED BANK (SINGAPORE) LIMITED',
  'MAYBANK SINGAPORE LIMITED',
  'HSBC BANK (SINGAPORE) LIMITED',
  'CITIBANK SINGAPORE LIMITED',
  'CIMB BANK BERHAD',
  'RHB BANK BERHAD',
  'BANK OF CHINA LIMITED',
  'INDUSTRIAL AND COMMERCIAL BANK OF CHINA LIMITED',
  'BANK OF INDIA',
  'INDIAN BANK',
  'INDIAN OVERSEAS BANK',
  'STATE BANK OF INDIA',
  'BANGKOK BANK PUBLIC COMPANY LIMITED',
  'MIZUHO BANK, LTD.',
  'MUFG BANK, LTD.',
  'SUMITOMO MITSUI BANKING CORPORATION',
  'DEUTSCHE BANK AG',
  'BNP PARIBAS',
  'AUSTRALIA AND NEW ZEALAND BANKING GROUP LIMITED',
  'HL BANK',
];

// Helper: fuzzy-match an OCR-extracted bank name to a catalog / fallback entry
const matchBankName = (ocrBankName, bankList = FALLBACK_SINGAPORE_BANKS) => {
  if (!ocrBankName) return null;
  const input = ocrBankName.toUpperCase().replace(/[^A-Z0-9]/g, '');
  // Exact match first
  for (const bank of bankList) {
    if (bank.toUpperCase().replace(/[^A-Z0-9]/g, '') === input) return bank;
  }
  // Partial match: OCR contains bank name or vice versa
  for (const bank of bankList) {
    const normalized = bank.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (input.includes(normalized) || normalized.includes(input)) return bank;
  }
  // Keyword match for common abbreviations and brand names.
  // Each value MUST exist verbatim in the bank list / catalog.
  // POSB → DBS BANK LTD (POSB is a DBS brand, not a separate legal entity).
  const keywordMap = {
    'DBS': 'DBS BANK LTD',
    'POSB': 'DBS BANK LTD',
    'OCBC': 'OVERSEA-CHINESE BANKING CORP LTD',
    'OVERSEACHINESE': 'OVERSEA-CHINESE BANKING CORP LTD',
    'UOB': 'UNITED OVERSEAS BANK LIMITED',
    'UNITEDOVERSEAS': 'UNITED OVERSEAS BANK LIMITED',
    'STANDARDCHARTERED': 'STANDARD CHARTERED BANK (SINGAPORE) LIMITED',
    'STANCHART': 'STANDARD CHARTERED BANK (SINGAPORE) LIMITED',
    'SCB': 'STANDARD CHARTERED BANK (SINGAPORE) LIMITED',
    'MAYBANK': 'MAYBANK SINGAPORE LIMITED',
    'MALAYANBANKING': 'MAYBANK SINGAPORE LIMITED',
    'HSBC': 'HSBC BANK (SINGAPORE) LIMITED',
    'HONGKONG': 'HSBC BANK (SINGAPORE) LIMITED',
    'CITIBANK': 'CITIBANK SINGAPORE LIMITED',
    'CITI': 'CITIBANK SINGAPORE LIMITED',
    'CIMB': 'CIMB BANK BERHAD',
    'RHB': 'RHB BANK BERHAD',
    'BANKOFCHINA': 'BANK OF CHINA LIMITED',
    'BOC': 'BANK OF CHINA LIMITED',
    'ICBC': 'INDUSTRIAL AND COMMERCIAL BANK OF CHINA LIMITED',
    'INDUSTRIALANDCOMMERCIAL': 'INDUSTRIAL AND COMMERCIAL BANK OF CHINA LIMITED',
    'BANKOFINDIA': 'BANK OF INDIA',
    'INDIANOVERSEAS': 'INDIAN OVERSEAS BANK',
    'STATEBANKOFINDIA': 'STATE BANK OF INDIA',
    'SBI': 'STATE BANK OF INDIA',
    'BANGKOK': 'BANGKOK BANK PUBLIC COMPANY LIMITED',
    'MIZUHO': 'MIZUHO BANK, LTD.',
    'MUFG': 'MUFG BANK, LTD.',
    'SMBC': 'SUMITOMO MITSUI BANKING CORPORATION',
    'SUMITOMO': 'SUMITOMO MITSUI BANKING CORPORATION',
    'DEUTSCHE': 'DEUTSCHE BANK AG',
    'BNP': 'BNP PARIBAS',
    'PARIBAS': 'BNP PARIBAS',
    'ANZ': 'AUSTRALIA AND NEW ZEALAND BANKING GROUP LIMITED',
    'AUSTRALIAANDNEWZEALAND': 'AUSTRALIA AND NEW ZEALAND BANKING GROUP LIMITED',
    'HLBANK': 'HL BANK',
  };
  for (const [keyword, bank] of Object.entries(keywordMap)) {
    if (input.includes(keyword)) return bank;
  }
  return null; // No match — will fall back to "Other"
};

// Single source of truth for the human-readable name of every supported
// document type. Used to build the modal header in BOTH modes:
//   employee → "Review <label>",  HR → "<label> Verification".
// Add new document types here so the header never falls back to a wrong/generic
// title (this map is what prevents a repeat of the "Driving Licence showing
// 'Review Child Certificate'" bug).
const DOCUMENT_TYPE_LABELS = {
  medical_certificate: 'Medical Certificate',
  marriage_certificate: 'Marriage Certificate',
  death_certificate: 'Death Certificate',
  driving_license: 'Driving Licence',
  interim_birth_notification: 'Interim Birth Notification',
  birth_certificate: 'Birth Certificate',
  id_card_front: 'ID Card',
  id_card_back: 'ID Card',
  passport: 'Passport',
  education_certificate: 'Education Certificate',
  professional_certificate: 'Professional Certificate',
  professional_card: 'Professional Card',
  bank_statement: 'Bank Statement',
};

// Upload-then-review OCR types that are still employee drafts: closing
// without submit deletes the server record so the uploads tab does not
// keep a "Pending Review" ghost. Family certificates and identity
// documents are not drafts — upload already hands them to HR.
const DRAFT_OCR_DOCUMENT_TYPES = [
  'education_certificate',
  'professional_certificate',
  'bank_statement',
];

const isDraftOcrDocumentType = (documentType) =>
  DRAFT_OCR_DOCUMENT_TYPES.includes(documentType);

/** Qualification certs have a dedicated delete endpoint; other drafts use DocumentApproval destroy. */
const getDraftDiscardAction = (documentType, documentApprovalId) => {
  if (['education_certificate', 'professional_certificate'].includes(documentType)) {
    return () => confidentialAPI.deleteQualificationDocument(documentApprovalId);
  }
  return () => confidentialAPI.deleteDocument(documentApprovalId);
};

/** Draft until submitted/finalised. Empty status (still loading) treated as draft after upload. */
const isUnsubmittedDraftStatus = (status) => {
  const s = (status || '').toString().toLowerCase();
  if (!s) return true;
  return s === 'pending' || s === 'ocr_completed';
};

// HR can complete verification from Employee Management even when the first
// OCR pass never persisted. processing / processing_ocr are leftover in-flight
// flags, not terminal states.
const HR_REVIEWABLE_STATUSES = [
  'pending',
  'ocr_completed',
  'submitted',
  'processing',
  'processing_ocr',
];

// Every OCR review this modal hosts. Interim birth notification is a manual
// HR form, not a scan. professional_card has no extractor.
const OCR_EXTRACT_AGAIN_TYPES = [
  'medical_certificate',
  'marriage_certificate',
  'death_certificate',
  'driving_license',
  'birth_certificate',
  'id_card_front',
  'id_card_back',
  'passport',
  'education_certificate',
  'professional_certificate',
  'bank_statement',
];

// Nationality suggestions for passport OCR (combobox — free typing still allowed,
// since a passport can carry any nationality). Superset of the id_card list.
const NATIONALITY_SUGGESTIONS = [
  'Singaporean', 'Malaysian', 'Chinese', 'Indian', 'Filipino', 'Indonesian',
  'Thai', 'Vietnamese', 'Burmese', 'Bangladeshi', 'Sri Lankan', 'Nepalese',
  'Pakistani', 'Cambodian', 'Taiwanese', 'Korean', 'Japanese', 'British',
  'American', 'Australian',
];

// Country suggestions for education / professional certificate OCR (combobox —
// free typing allowed for any country not listed).
const COUNTRY_SUGGESTIONS = [
  'Singapore', 'Malaysia', 'China', 'India', 'Philippines', 'Indonesia',
  'Thailand', 'Vietnam', 'Myanmar', 'Bangladesh', 'Sri Lanka', 'United Kingdom',
  'United States', 'Australia', 'Canada', 'New Zealand', 'Japan', 'South Korea',
  'Taiwan', 'Hong Kong',
];

// Singapore driving licence classes accepted by the DrivingLicense model
const DRIVING_LICENSE_CLASS_OPTIONS = [
  { value: '2b', label: 'Class 2B (Motorcycle ≤200cc)' },
  { value: '2a', label: 'Class 2A (Motorcycle ≤400cc)' },
  { value: '2', label: 'Class 2 (Motorcycle >400cc)' },
  { value: '3', label: 'Class 3 (Car)' },
  { value: '3a', label: 'Class 3A (Automatic Car)' },
  { value: '3c', label: 'Class 3C (Light Goods Vehicle)' },
  { value: '4', label: 'Class 4 (Heavy Goods Vehicle)' },
  { value: '4a', label: 'Class 4A (Heavy Goods Vehicle - Manual)' },
  { value: '5', label: 'Class 5 (Heavy Vehicle)' },
  { value: '5a', label: 'Class 5A (Heavy Vehicle - Manual)' },
];

// Normalise a single driving-licence class row coming from the backend/n8n into
// the shape the modal's editable form uses. Lifetime is derived from a missing
// expiry date so each class card renders its own issue/expiry/number cleanly.
const normalizeDrivingLicenseRow = (row = {}) => {
  const expiry = (row.expiry_date || '').toString().trim();
  const isLifetime = (row.is_lifetime === true) || expiry === '';
  return {
    license_class: (row.license_class || '').toString().trim().toLowerCase(),
    issue_date: (row.issue_date || '').toString().trim(),
    expiry_date: isLifetime ? '' : expiry,
    license_number: (row.license_number || '').toString().trim(),
    notes: (row.notes || '').toString().trim(),
    is_lifetime: isLifetime,
  };
};

const DocumentVerificationModal = ({
  isOpen,
  onClose,
  documentApprovalId,
  documentType = 'birth_certificate', // 'birth_certificate' or 'marriage_certificate'
  mode = 'employee', // 'employee' or 'hr'
  viewMode = 'edit', // 'edit' or 'view' - determines if document is editable or view-only
  onApprovalComplete,
  ocrData: initialOcrData = {}, // Accept OCR data from parent
  employeeProfile = null, // Employee profile data for auto-detection
  employeeName: employeeNameProp = null,   // Optional: display name for modal subtitle
  employeeStaffCode: employeeStaffCodeProp = null, // Optional: staff code for modal subtitle
  detectedRole = null, // Role detected in background (bridegroom/bride)
  applicationId: applicationIdProp = null // For medical_certificate: leave_application_id
}) => {
  
  console.log('🔍 DocumentVerificationModal props:', {
    isOpen,
    documentApprovalId,
    documentType,
    mode,
    viewMode,
    initialOcrDataKeys: Object.keys(initialOcrData),
    employeeProfile: employeeProfile ? {
      full_name: employeeProfile.full_name,
      name: employeeProfile.name,
      gender: employeeProfile.gender,
      hrprofile_gender: employeeProfile.hrprofile_gender
    } : null
  });
  

  const isMobile = useIsMobile();
  const resolvedTheme = mode === 'hr' ? 'admin' : 'employee';
  const { data: singaporeBanksCatalog = [] } = useSingaporeBanks({
    enabled: isOpen && documentType === 'bank_statement',
  });
  const singaporeBankNames = singaporeBanksCatalog.length
    ? singaporeBanksCatalog.map((b) => b.legal_name)
    : FALLBACK_SINGAPORE_BANKS;
  const [activeTab, setActiveTab] = useState('document');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [documentData, setDocumentData] = useState(null);
  const [ocrData, setOcrData] = useState({});
  const [detectedScheme, setDetectedScheme] = useState(null);
  const [existingProfileData, setExistingProfileData] = useState(null);
  const [selectedValues, setSelectedValues] = useState({}); // Conflict resolution: {'field_key': 'ocr' | 'existing'}
  // Driving licence is multi-record (one image → many classes), so it has its own
  // working array instead of the flat ocrData map used by single-record documents.
  const [drivingLicenseClasses, setDrivingLicenseClasses] = useState([]);

  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState(null);

  // Refs to avoid stale closures and track state for cleanup
  const blobUrlRef = useRef(null);
  const abortControllerRef = useRef(null);
  const blobAbortControllerRef = useRef(null);
  const currentFileUrlRef = useRef(null);
  // True once any OCR field has been edited — used to gate the accidental-dismissal confirm
  const isDirtyRef = useRef(false);
  // True once the document has been approved/rejected/submitted — skip confirm on close
  const wasFinalisedRef = useRef(false);

  const viewer = useImageViewer();

  // Download the currently-previewed document. PDFs get the browser's native
  // toolbar, but the <img> viewer has none — HR needs to save the original
  // (zoom in their OS viewer, keep for records, cross-check the employee file).
  const handleDownloadDocument = () => {
    if (!documentBlobUrl) return;
    const rawName = documentData?.document_info?.filename || 'document';
    // Strip any path and keep a clean basename for the saved file.
    const filename = rawName.split('/').pop().split('\\').pop() || 'document';
    const link = document.createElement('a');
    link.href = documentBlobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeConfirmMessage, setCloseConfirmMessage] = useState('');
  const { isDiscarding, safeDiscardAndClose } = useSafeDiscardAndClose();

  const normalizeOcrData = (data) => {
    if (!data || typeof data !== 'object') return {};
    const normalized = { ...data };

    // Normalize child citizenship status
    if (normalized.child_citizenship_status !== undefined && normalized.child_citizenship_status !== null) {
      const raw = normalized.child_citizenship_status.toString().trim().toLowerCase();

      if (['yes', 'y', 'singapore_citizen', 'singapore citizen', 'citizen'].includes(raw)) {
        normalized.child_citizenship_status = 'yes';
      } else if (['no', 'n', 'not_singapore_citizen', 'not singapore citizen', 'non-citizen', 'non citizen'].includes(raw)) {
        normalized.child_citizenship_status = 'no';
      } else {
        normalized.child_citizenship_status = raw;
      }
    }

    // Normalize ID card OCR data: map backend fields (ocr_*) to frontend fields
    if (documentType === 'id_card_front' || documentType === 'id_card_back') {
      const fieldMapping = {
        'ocr_id_card_name': 'id_card_name',
        'ocr_id_card_nric_fin_number': 'id_card_nric_fin_number',
        'ocr_id_card_wp_number': 'id_card_wp_number',
        'ocr_id_card_type': 'id_card_type',
        'ocr_id_card_issue_date': 'id_card_issue_date',
        'ocr_id_card_nationality': 'id_card_nationality',
        'ocr_id_card_race': 'id_card_race',
        'ocr_id_card_dob': 'id_card_dob',
        'ocr_id_card_sex': 'id_card_sex',
        'ocr_occupation': 'occupation',
        'ocr_id_card_application_date': 'date_of_application',
        'ocr_id_card_expiry_date': 'date_of_expiry',
        'ocr_id_card_color': 'id_card_color',
        'ocr_employer_name': 'employer_name',
        'ocr_sector': 'sector',
        'ocr_card_serial_number': 'card_serial_number',
        'ocr_id_card_address': 'id_card_address',
        'ocr_id_card_postal_code': 'id_card_postal_code',
        'ocr_id_card_residential_unit_number': 'id_card_residential_unit_number',
      };

      // Map backend fields to frontend fields
      for (const [backendField, frontendField] of Object.entries(fieldMapping)) {
        if (normalized[backendField] !== undefined && normalized[backendField] !== null && normalized[backendField] !== '') {
          // Only set if frontend field doesn't already have a value (prioritize first match)
          if (normalized[frontendField] === undefined || normalized[frontendField] === null || normalized[frontendField] === '') {
            let value = normalized[backendField];
            
            // Normalize specific fields to match dropdown option values
            if (frontendField === 'id_card_race' && value) {
              // Convert to lowercase and map common variations
              const raceValue = String(value).trim().toLowerCase();
              const raceMapping = {
                'chinese': 'chinese',
                'malay': 'malay',
                'indian': 'indian',
                'eurasian': 'eurasian',
                'indonesian': 'indonesian',
                'others': 'others',
                'other': 'others',
                'not disclosed': 'not_disclosed',
                'prefer not to disclose': 'not_disclosed'
              };
              normalized[frontendField] = raceMapping[raceValue] || raceValue;
            } else if (frontendField === 'id_card_sex' && value) {
              // Normalize gender: ensure uppercase M/F/O
              const genderValue = String(value).trim().toUpperCase();
              if (['M', 'F', 'O', 'MALE', 'FEMALE', 'OTHER'].includes(genderValue)) {
                normalized[frontendField] = genderValue === 'MALE' ? 'M' : 
                                            genderValue === 'FEMALE' ? 'F' : 
                                            genderValue === 'OTHER' ? 'O' : genderValue;
              } else {
                normalized[frontendField] = value;
              }
            } else if (frontendField === 'id_card_nationality' && value) {
              // Map country names / ISO codes / demonyms to the canonical
              // dropdown value via the shared normalizer (single source of truth).
              normalized[frontendField] = normalizeNationality(value);
            } else if (frontendField === 'id_card_type' && value) {
              // Normalize ID card type to lowercase
              const typeValue = String(value).trim().toLowerCase();
              const typeMapping = {
                'work permit': 'wp',
                'employment pass': 'ep',
                's pass': 's_pass',
                's-pass': 's_pass',
                'permanent resident': 'pr',
                'singapore citizen': 'citizen',
                'citizen': 'citizen',
                'long term visit pass': 'ltvp',
                'dependant pass': 'dp'
              };
              normalized[frontendField] = typeMapping[typeValue] || typeValue;
            } else {
              normalized[frontendField] = value;
            }
          }
          
          // Keep backend field for backwards compatibility, but prefer frontend field
          if (normalized[frontendField] !== undefined) {
            delete normalized[backendField];
          }
        }
      }

      // Defensive compatibility for OCR payloads that duplicate an S Pass or
      // Work Permit number into the FIN slot. Use the same semantic contract
      // as the backend so HR only resolves genuine, visible profile conflicts.
      const identifiers = normalizeIdCardIdentifiers(
        normalized.id_card_type,
        normalized.id_card_nric_fin_number,
        normalized.id_card_wp_number
      );
      normalized.id_card_nric_fin_number = identifiers.nricFinNumber;
      normalized.id_card_wp_number = identifiers.workPassNumber;
    }

    // Normalize passport OCR nationality: passports report the country name or
    // the ISO alpha-3 code in the MRZ (e.g. MYANMAR / MMR), but the profile
    // stores the demonym (Burmese). Canonicalise so HR sees a value consistent
    // with the employee record and conflict detection doesn't false-positive.
    if (documentType === 'passport' && normalized.ocr_passport_nationality) {
      normalized.ocr_passport_nationality = normalizeNationality(normalized.ocr_passport_nationality);
    }

    // Normalize bank statement OCR: auto-match bank name to dropdown option
    if (documentType === 'bank_statement' && normalized.ocr_bank_name) {
      const matched = matchBankName(normalized.ocr_bank_name, singaporeBankNames);
      if (matched) {
        normalized.ocr_bank_name = matched;
      }
      // If no match, leave as-is — the dropdown will show "Other" with the raw value
    }

    return normalized;
  };
  
  // ── Interim Birth Notification form state ────────────────────────────────
  const [interimForm, setInterimForm] = useState({
    spouse_name: '',
    child_date_of_birth: '',
    hospital: '',
    hospital_other: '',
    hospital_other_mode: false,
    child_citizenship_at_birth: '',   // 'yes' | 'no'
    source_document_type: '',
    source_document_type_other: '',
    hr_notes: '',
  });
  const [interimSaving, setInterimSaving] = useState(false);

  // Marriage certificate workflow states
  const [workflowStep, setWorkflowStep] = useState('review'); // 'review', 'submitted', 'verified'
  const [webhookCalled, setWebhookCalled] = useState(false); // Track if webhook has been called to prevent duplicate calls
  const [rerunningOcr, setRerunningOcr] = useState(false); // Stored-file OCR re-run in progress
  const autoPassportOcrAttemptedRef = useRef(null);
  
  // Smart role detection for marriage certificates with gender validation
  const getEmployeeRole = () => {
    // HR reviewers should not run employee role detection UI logic
    if (mode === 'hr') return null;
    if (!employeeProfile || documentType !== 'marriage_certificate') return null;
    
    // Get employee name from profile
    const employeeName = (employeeProfile?.full_name || employeeProfile?.name || '').toLowerCase().trim();
    
    // Get names from OCR data
    const bridegroomName = (ocrData?.bridegroom_full_name || ocrData?.groom_name || '').toLowerCase().trim();
    const brideName = (ocrData?.bride_full_name || ocrData?.bride_name || '').toLowerCase().trim();
    
    // Get employee gender for validation
    const gender = employeeProfile?.gender || employeeProfile?.hrprofile_gender;
    
    console.log('🔍 Role detection debug:', {
      employeeName,
      bridegroomName,
      brideName,
      gender,
      ocrDataKeys: Object.keys(ocrData),
      employeeProfile: employeeProfile?.full_name || employeeProfile?.name
    });
    
    // Helper function for name matching (exact or partial)
    const nameMatches = (name1, name2) => {
      if (!name1 || !name2) return false;
      if (name1 === name2) return true;
      
      const words1 = name1.split(' ').filter(w => w.length > 2);
      const words2 = name2.split(' ').filter(w => w.length > 2);
      return words1.some(word => 
        words2.some(ocrWord => ocrWord.includes(word) || word.includes(ocrWord))
      );
    };
    
    // Check bridegroom match with gender cross-validation
    if (nameMatches(employeeName, bridegroomName)) {
      const genderMatch = gender === 'male' || gender === 'M';
      console.log('🔍 Bridegroom name match:', { 
        employeeName, 
        bridegroomName, 
        gender,
        genderMatch: genderMatch ? '✓ Matches (male)' : '⚠ Mismatch (not male)'
      });
      return 'bridegroom'; // Always return bridegroom if name matches
    }
    
    // Check bride match with gender cross-validation
    if (nameMatches(employeeName, brideName)) {
      const genderMatch = gender === 'female' || gender === 'F';
      console.log('🔍 Bride name match:', { 
        employeeName, 
        brideName, 
        gender,
        genderMatch: genderMatch ? '✓ Matches (female)' : '⚠ Mismatch (not female)'
      });
      return 'bride'; // Always return bride if name matches
    }
    
    // Only use gender fallback if NO name matches
    console.log('🔍 No name matches found, using gender fallback:', { gender });
    if (gender === 'male' || gender === 'M') return 'bridegroom';
    if (gender === 'female' || gender === 'F') return 'bride';
    
    return null; // Can't determine role
  };
  
  // State for manually selected role
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Bank name dropdown: track when user explicitly selects "Other"
  const [bankNameOtherMode, setBankNameOtherMode] = useState(false);
  
  // Get the final employee role (auto-detected or manually selected)
  const getFinalEmployeeRole = () => {
    console.log('🔍 getFinalEmployeeRole called:', { selectedRole, detectedRole, employeeProfile: !!employeeProfile });
    if (selectedRole) return selectedRole;
    if (detectedRole) return detectedRole; // Use role detected in background
    const detected = getEmployeeRole();
    console.log('🔍 getEmployeeRole result:', detected);
    return detected;
  };
  
  const getSpouseInfo = () => {
    const role = getFinalEmployeeRole();
    if (!role || !ocrData) return null;
    
    if (role === 'bridegroom') {
      return {
        name: ocrData.bride_full_name || ocrData.bride_name || 'Not detected',
        role: 'Bride'
      };
    } else if (role === 'bride') {
      return {
        name: ocrData.bridegroom_full_name || ocrData.groom_name || 'Not detected',
        role: 'Bridegroom'
      };
    }
    return null;
  };
  
  const employeeRole = getFinalEmployeeRole();
  const spouseInfo = getSpouseInfo();

  // Get API endpoints based on document type
  const getApiEndpoints = () => {
    console.log('🔍 getApiEndpoints called with documentType:', documentType);
    
    switch (documentType) {
      case 'medical_certificate':
        console.log('🔍 Using medical certificate endpoints');
        // Medical certificate verification uses applicationId and dedicated endpoints
        return {
          mcVerificationByApplicationId: true,
          verify: '/mc/verify-and-approve/',
          reject: '/mc/reject/',
          updateField: '/mc/update-field/'
        };
      case 'marriage_certificate':
        console.log('🔍 Using marriage certificate endpoints');
        return {
          verification: `/marriage-certificate/verification/${documentApprovalId}/`,
          editOcr: '/marriage-certificate/edit-ocr/',
          verify: '/marriage-certificate/verify/',
          submitAfterReview: '/marriage-certificate/submit-after-review/'
        };
        
      case 'death_certificate':
        console.log('🔍 Using death certificate endpoints');
        return {
          verification: `/death-certificate/verification/${documentApprovalId}/`,
          editOcr: '/death-certificate/edit-ocr/',
          verify: '/death-certificate/verify/'
        };

      case 'driving_license':
        console.log('🔍 Using driving licence endpoints');
        return {
          verification: `/driving-license/verification/${documentApprovalId}/`,
          editOcr: '/driving-license/edit-ocr/',
          verify: '/driving-license/verify/',
          submitAfterReview: '/driving-license/submit/'
        };

      case 'interim_birth_notification':
        return {
          verification: `/interim-birth-notification/verification/${documentApprovalId}/`,
          verify: '/interim-birth-notification/verify/',
        };

      case 'birth_certificate':
        console.log('🔍 Using child certificate endpoints');
        return {
          verification: `/child-certificate/verification/${documentApprovalId}/`,
          editOcr: '/child-certificate/edit-ocr/',
          verify: '/child-certificate/verify/',
          submitAfterReview: '/child-certificate/submit-after-review/'
        };
        
      case 'id_card_front':
      case 'id_card_back':
        console.log('🔍 Using ID card endpoints');
        return {
          verification: `/id-card/verification/${documentApprovalId}/`,
          editOcr: '/id-card/edit-ocr/',
          verify: '/id-card/verify/',
          submitAfterReview: '/id-card/submit/'
        };
        
      case 'passport':
        console.log('🔍 Using passport endpoints');
        return {
          verification: `/passport/verification/${documentApprovalId}/`,
          editOcr: '/passport/edit-ocr/',
          verify: '/passport/verify/',
          submitAfterReview: '/passport/submit/'
        };
        
      case 'education_certificate':
      case 'professional_certificate':
        console.log('🔍 Using qualification certificate endpoints');
        return {
          verification: `/qualification/verification/${documentApprovalId}/`,
          editOcr: '/qualification/edit-ocr/',
          verify: '/qualification/verify/',
          submitAfterReview: '/qualification/submit/'
        };
        
      case 'professional_card':
        console.log('🔍 Using professional card endpoints');
        return {
          verification: `/professional-card/verification/${documentApprovalId}/`,
          editOcr: '/professional-card/edit-ocr/',
          verify: '/professional-card/verify/'
        };
        
      case 'bank_statement':
        console.log('🔍 Using bank statement endpoints');
        return {
          verification: `/bank-statement/verification/${documentApprovalId}/`,
          editOcr: '/bank-statement/edit-ocr/',
          verify: '/bank-statement/verify/',
          submitAfterReview: '/bank-statement/submit/'
        };
        
      default:
        console.error('🔍 Unsupported document type:', documentType);
        throw new Error(`Document verification not supported for type: ${documentType}`);
    }
  };

  // Helper function to check if field_of_study should be required (higher education only)
  const isFieldOfStudyRequired = (ocrData) => {
    const qualificationName = (ocrData.qualification_name || '').toLowerCase();
    const higherEdKeywords = ['diploma', 'bachelor', 'master', 'phd', 'doctorate', 'degree', 'graduate'];
    return higherEdKeywords.some(keyword => qualificationName.includes(keyword));
  };

  // Get field configuration based on document type
  const getFieldConfig = () => {
    switch (documentType) {
      case 'medical_certificate':
        return [
          { key: 'ocr_patient_full_name', label: 'Patient Full Name', icon: User, required: true },
          { key: 'ocr_patient_nric_fin', label: 'NRIC/FIN', icon: FileText, required: true },
          { key: 'ocr_mc_number', label: 'MC Number', icon: FileText, required: false },
          { key: 'ocr_clinic_hospital_name', label: 'Clinic/Hospital', icon: Building, required: false, type: 'combobox', suggestions: SINGAPORE_HOSPITALS },
          { key: 'ocr_doctor_name', label: 'Doctor Name', icon: User, required: false },
          { key: 'ocr_mc_issue_date', label: 'Issue Date', icon: Calendar, type: 'date', required: false },
          { key: 'ocr_mc_start_date', label: 'Start Date', icon: Calendar, type: 'date', required: true },
          { key: 'ocr_mc_end_date', label: 'End Date', icon: Calendar, type: 'date', required: true },
          { key: 'ocr_mc_total_days', label: 'Total Days', icon: Clock, required: true },
          { key: 'ocr_diagnosis', label: 'Diagnosis/Remarks', icon: FileText, required: false },
        ];
      case 'marriage_certificate':
        return [
          { key: 'bridegroom_full_name', label: 'Bridegroom Full Name', icon: User, required: true },
          { key: 'bride_full_name', label: 'Bride Full Name', icon: User, required: true },
          { key: 'marriage_date', label: 'Marriage Date', icon: Calendar, type: 'date', required: true },
          { key: 'marriage_certificate_number', label: 'Certificate Number', icon: FileText, required: false }
        ];

      case 'death_certificate':
        return [
          { key: 'deceased_name', label: 'Deceased Full Name', icon: User, required: true },
          { key: 'date_of_death', label: 'Date of Death', icon: Calendar, type: 'date', required: true },
          { key: 'certificate_number', label: 'Certificate Number', icon: FileText, required: false },
          { key: 'date_of_issue', label: 'Date of Issue', icon: Calendar, type: 'date', required: false },
          { key: 'deceased_nric_last4', label: 'Deceased NRIC (last 4)', icon: FileText, required: false },
          { key: 'deceased_sex', label: 'Gender', icon: User, type: 'select', required: false, options: [
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' }
          ]},
          { key: 'deceased_dob', label: 'Deceased Date of Birth', icon: Calendar, type: 'date', required: false }
        ];
        
      case 'birth_certificate':
        return [
          { key: 'child_full_name', label: 'Child Full Name', icon: Baby, required: true },
          { key: 'child_date_of_birth', label: 'Date of Birth', icon: Calendar, type: 'date', required: true },
          { key: 'child_citizenship_status', label: 'Singapore Citizen?', icon: Shield, type: 'select', required: true, options: ['yes', 'no'] },
          { key: 'mother_full_name', label: 'Mother Full Name', icon: User, required: false },
          { key: 'father_full_name', label: 'Father Full Name', icon: User, required: false },
          { key: 'birth_certificate_number', label: 'Birth Certificate Number', icon: FileText, required: true }
        ];
        
      case 'id_card_front': {
        const idCardType = String(ocrData?.id_card_type || '').toLowerCase();
        const nameLabel = idCardType === 's_pass'
          ? 'Name as per S Pass'
          : idCardType === 'wp'
          ? 'Name as per Work Permit'
          : 'Name as per NRIC/FIN';
        const workPassNumberLabel = idCardType === 's_pass'
          ? 'S Pass Number'
          : 'Work Permit Number';
        return [
          { key: 'id_card_type', label: 'ID Card Type', icon: CreditCard, type: 'select', required: true, options: [
            { value: 'citizen', label: 'Singapore Citizen' },
            { value: 'pr', label: 'Permanent Resident' },
            { value: 'ep', label: 'Employment Pass' },
            { value: 's_pass', label: 'S Pass' },
            { value: 'wp', label: 'Work Permit' },
            { value: 'ltvp', label: 'Long Term Visit Pass' },
            { value: 'dp', label: 'Dependant Pass' },
            { value: 'other', label: 'Other' }
          ]},
          { key: 'id_card_name', label: nameLabel, icon: User, required: true },
          { key: 'id_card_nric_fin_number', label: 'NRIC/FIN Number', icon: FileText, required: true },
          { key: 'id_card_dob', label: 'Date of Birth', icon: Calendar, type: 'date', required: true },
          { key: 'id_card_sex', label: 'Gender', icon: User, type: 'select', required: true, options: [
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' }
          ]},
          { key: 'id_card_nationality', label: 'Nationality', icon: Globe, type: 'select', required: false, options: [
            { value: 'Singaporean', label: 'Singaporean' },
            { value: 'Malaysian', label: 'Malaysian' },
            { value: 'Chinese', label: 'Chinese' },
            { value: 'Indian', label: 'Indian' },
            { value: 'Filipino', label: 'Filipino' },
            { value: 'Indonesian', label: 'Indonesian' },
            { value: 'Thai', label: 'Thai' },
            { value: 'Vietnamese', label: 'Vietnamese' },
            { value: 'Burmese', label: 'Burmese' },
            { value: 'Bangladeshi', label: 'Bangladeshi' },
            { value: 'Other', label: 'Other' }
          ]},
          { key: 'id_card_race', label: 'Race', icon: User, type: 'select', required: false, options: RACE_OPTIONS },
          { key: 'id_card_wp_number', label: workPassNumberLabel, icon: FileText, required: true },
          { key: 'card_serial_number', label: 'Card Serial Number', icon: Tag, required: false },
          { key: 'id_card_issue_date', label: 'Issue Date', icon: Calendar, type: 'date', required: false },
          { key: 'occupation', label: 'Occupation', icon: Building, required: false },
          { key: 'date_of_application', label: 'Application Date', icon: Calendar, type: 'date', required: false },
          { key: 'date_of_expiry', label: 'Expiry Date', icon: Calendar, type: 'date', required: false }
        ];
      }
        
      case 'id_card_back':
        return [
          { key: 'id_card_name', label: 'Full Name', icon: User, required: false },
          { key: 'id_card_nric_fin_number', label: 'NRIC/FIN Number', icon: FileText, required: true },
          { key: 'id_card_sex', label: 'Gender', icon: User, type: 'select', required: false, options: [
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' }
          ]},
          { key: 'id_card_issue_date', label: 'Issue Date', icon: Calendar, type: 'date', required: false },
          { key: 'id_card_nationality', label: 'Nationality', icon: Globe, type: 'select', required: false, options: [
            { value: 'Singaporean', label: 'Singaporean' },
            { value: 'Malaysian', label: 'Malaysian' },
            { value: 'Chinese', label: 'Chinese' },
            { value: 'Indian', label: 'Indian' },
            { value: 'Filipino', label: 'Filipino' },
            { value: 'Indonesian', label: 'Indonesian' },
            { value: 'Thai', label: 'Thai' },
            { value: 'Vietnamese', label: 'Vietnamese' },
            { value: 'Burmese', label: 'Burmese' },
            { value: 'Bangladeshi', label: 'Bangladeshi' },
            { value: 'Other', label: 'Other' }
          ]},
          { key: 'occupation', label: 'Occupation', icon: Building, required: false },
          { key: 'date_of_application', label: 'Application Date', icon: Calendar, type: 'date', required: false },
          { key: 'date_of_expiry', label: 'Expiry Date', icon: Calendar, type: 'date', required: false },
          { key: 'id_card_address', label: 'Address', icon: MapPin, required: false },
          { key: 'id_card_postal_code', label: 'Postal Code', icon: MapPin, required: false },
          { key: 'id_card_residential_unit_number', label: 'Unit Number', icon: MapPin, required: false }
        ];
        
      case 'passport':
        return [
          { key: 'ocr_passport_first_name', label: 'First Name (Given Name)', icon: User, required: true },
          { key: 'ocr_passport_last_name', label: 'Last Name (Surname)', icon: User, required: false },
          { key: 'ocr_passport_number', label: 'Passport Number', icon: FileText, required: true },
          { key: 'ocr_passport_nationality', label: 'Nationality', icon: Globe, required: true, type: 'combobox', suggestions: NATIONALITY_SUGGESTIONS },
          { key: 'ocr_passport_date_of_birth', label: 'Date of Birth', icon: Calendar, type: 'date', required: true },
          { key: 'ocr_passport_gender', label: 'Gender', icon: Shield, required: true, type: 'select', options: [
            { value: 'M', label: 'Male' },
            { value: 'F', label: 'Female' },
            { value: 'O', label: 'Other' }
          ]},
          { key: 'ocr_passport_expiry_date', label: 'Passport Expiry Date', icon: Calendar, type: 'date', required: true }
        ];
        
      case 'education_certificate':
        return [
          { key: 'institution_name', label: 'Institution Name', icon: GraduationCap, required: true },
          { key: 'qualification_name', label: 'Qualification Name', icon: Award, required: true },
          { key: 'field_of_study', label: 'Field of Study', icon: BookOpen, required: false, conditionallyRequired: true },
          { key: 'certificate_number', label: 'Certificate Number', icon: FileText, required: false },
          { key: 'start_year', label: 'Start Year', icon: Calendar, type: 'number', required: false },
          { key: 'end_year', label: 'End Year', icon: Calendar, type: 'number', required: true },
          { key: 'country', label: 'Country', icon: MapPin, required: false, type: 'combobox', suggestions: COUNTRY_SUGGESTIONS }
        ];
        
      case 'professional_certificate':
        return [
          { key: 'cert_name', label: 'Certificate Name', icon: Award, required: true },
          { key: 'issuing_organization', label: 'Issuing Organization', icon: Building, required: true },
          { 
            key: 'cert_category', 
            label: 'Category', 
            icon: Tag, 
            type: 'select', 
            required: false,
            options: [
              { value: 'safety', label: 'Safety & Health' },
              { value: 'construction', label: 'Construction & Building' },
              { value: 'project_management', label: 'Project Management' },
              { value: 'quality', label: 'Quality Management' },
              { value: 'environmental', label: 'Environmental' },
              { value: 'it_tech', label: 'IT & Technology' },
              { value: 'finance', label: 'Finance & Accounting' },
              { value: 'hr', label: 'Human Resources' },
              { value: 'legal', label: 'Legal & Compliance' },
              { value: 'trade_skill', label: 'Trade Skills' },
              { value: 'multi_skilling', label: 'Multi-Skilling' },
              { value: 'other', label: 'Other' }
            ]
          },
          { key: 'certificate_number', label: 'Certificate Number', icon: FileText, required: false },
          { key: 'issue_date', label: 'Issue Date', icon: Calendar, type: 'date', required: true },
          { key: 'expiry_date', label: 'Expiry Date', icon: Calendar, type: 'date', required: false },
          { key: 'cert_country', label: 'Country', icon: MapPin, required: false, type: 'combobox', suggestions: COUNTRY_SUGGESTIONS }
        ];
        
      case 'professional_card':
        return [
          { key: 'card_type', label: 'Card Type', icon: CreditCard, required: true },
          { key: 'card_number', label: 'Card Number', icon: FileText, required: true },
          { key: 'issuing_authority', label: 'Issuing Authority', icon: Building, required: true },
          { key: 'issue_date', label: 'Issue Date', icon: Calendar, type: 'date', required: false },
          { key: 'expiry_date', label: 'Expiry Date', icon: Calendar, type: 'date', required: false }
        ];

      case 'bank_statement':
        return [
          { key: 'ocr_bank_name', label: 'Bank Name', icon: Building, required: true },
          { key: 'ocr_bank_account_number', label: 'Account Number', icon: CreditCard, required: true },
          { key: 'ocr_branch_code', label: 'Branch Code', icon: Tag, required: false },
          { key: 'ocr_account_holder_name', label: 'Account Holder Name', icon: User, required: true }
        ];
        
      default:
        console.error('🔍 No field configuration for document type:', documentType);
        return [];
    }
  };

  // Conflict detection function using normalization utilities
  const detectConflicts = (ocrData, existingProfile) => {
    if (!ocrData || !existingProfile || typeof ocrData !== 'object' || typeof existingProfile !== 'object') {
      return {};
    }

    // Map OCR field keys to profile field keys based on document type
    let fieldMapping = {};
    
    if (documentType === 'passport') {
      fieldMapping = {
        'ocr_passport_number': 'passport_number',
        'ocr_passport_nationality': 'nationality',
        'ocr_passport_date_of_birth': 'date_of_birth',
        'ocr_passport_gender': 'gender',
        'ocr_passport_expiry_date': 'passport_expiry_date',
      };
    } else {
      // Default: ID card field mapping
      fieldMapping = {
        'id_card_name': 'name_as_per_nric_fin',
        'id_card_nric_fin_number': 'nric_fin',
        'id_card_dob': 'dob',
        'id_card_sex': 'gender',
        'id_card_nationality': 'nationality',
        'id_card_race': 'race',
        'id_card_type': 'employment_pass_type',
        'id_card_issue_date': 'pass_issue_date',
        'id_card_wp_number': 'wp_number',
        'id_card_address': 'residential_address',
        'id_card_postal_code': 'residential_postal_code',
        'id_card_residential_unit_number': 'residential_unit_number',
        'occupation': 'occupation',
        'date_of_application': 'wp_application_date',
        'date_of_expiry': 'pass_expiry_date',
      };
    }

    const conflicts = detectConflictsUtil(ocrData, existingProfile, fieldMapping);

    // Banner, pickers, and approve-blocking must share one visibility
    // contract. The shared ID-card mapping still compares front-only
    // fields (DOB, race, pass type, WP number) when this is the back —
    // a visit pass back extracts those, but the back form never shows them.
    const visibleFieldKeys = new Set(
      getFieldConfig()
        .filter(field => isFieldVisible(field, ocrData))
        .map(field => field.key)
    );
    return filterConflictsToVisibleFields(conflicts, visibleFieldKeys);
  };

  const handleClose = () => {
    // If already finalised (approved / verified / rejected / submitted) — close immediately, no confirm needed
    const currentStatus = (documentData?.document_info?.status || documentData?.status || '').toLowerCase();
    const isAlreadyFinalised = ['approved', 'verified', 'rejected'].includes(currentStatus);

    if (wasFinalisedRef.current || isAlreadyFinalised) {
      cleanup();
      onClose();
      return;
    }

    // HR mode: data is loaded and not yet finalised — any close risks losing edits
    if (mode === 'hr' && documentData && isDirtyRef.current) {
      setCloseConfirmMessage('You have unsaved field edits. Closing now will discard them.');
      setShowCloseConfirm(true);
      return;
    }

    // Employee mode: discard unsubmitted cert / bank-statement drafts.
    // Identity documents are not drafts — upload already handed them to HR.
    const hasDocument =
      documentApprovalId &&
      isUnsubmittedDraftStatus(currentStatus) &&
      isDraftOcrDocumentType(documentType);

    if (hasDocument && mode === 'employee') {
      const hasOcrData = ocrData && Object.keys(ocrData).length > 0 && 
                         Object.values(ocrData).some(val => val !== null && val !== '' && val !== undefined);
      
      const message = hasOcrData
        ? 'You have uploaded a document and OCR processing is complete, but you haven\'t submitted it for review. Closing now will remove the document and all OCR data. You will need to re-upload and re-process if you want to submit later.'
        : 'You have uploaded a document but haven\'t submitted it for review. Closing now will remove the uploaded document. Do you want to continue?';
      
      setCloseConfirmMessage(message);
      setShowCloseConfirm(true);
      return;
    }

    // Employee mode: warn if they've edited OCR fields but haven't submitted yet
    if (mode === 'employee' && isDirtyRef.current) {
      setCloseConfirmMessage('You have unsaved field edits. Closing now will discard them.');
      setShowCloseConfirm(true);
      return;
    }
    
    cleanup();
    onClose();
  };

  const handleConfirmClose = async () => {
    // Only attempt document deletion for the employee flow where a draft document was uploaded
    // but not yet submitted. For HR mode or plain "unsaved edits" cases, just close.
    const currentStatus = documentData?.document_info?.status || documentData?.status || '';
    const shouldDeleteDocument =
      mode === 'employee' &&
      documentApprovalId &&
      isUnsubmittedDraftStatus(currentStatus) &&
      isDraftOcrDocumentType(documentType);

    if (!shouldDeleteDocument) {
      setShowCloseConfirm(false);
      cleanup();
      onClose();
      return;
    }

    await safeDiscardAndClose({
      discardAction: getDraftDiscardAction(documentType, documentApprovalId),
      onBeforeClose: () => setShowCloseConfirm(false),
      onDiscardSuccess: () => toast.success('Document removed'),
      onClose: ({ discardSucceeded }) => {
        cleanup();
        if (discardSucceeded && onApprovalComplete) {
          onApprovalComplete({ success: true, deleted: true, documentApprovalId });
          return;
        }
        onClose();
      },
      failureMessage: 'Failed to remove document. You can delete it manually from your uploads.',
      logContext: 'Failed to delete document during modal close:',
    });
  };

  // Cleanup function for blob URLs and abort controllers
  const cleanup = () => {
    // Cancel any in-flight data fetch requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Cancel any in-flight blob load requests
    if (blobAbortControllerRef.current) {
      blobAbortControllerRef.current.abort();
      blobAbortControllerRef.current = null;
    }
    
    // Clean up blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setDocumentBlobUrl(null);
    }
    
    // Reset state — clear stale documentData so the blob loader doesn't
    // prematurely cancel a fresh fetchDocumentData request on next open
    setDocumentData(null);
    setLoadingDocument(false);
    setDocumentError(null);
    setShowRejectForm(false);
    setRejectionReason('');
    currentFileUrlRef.current = null;
  };

  // Main effect: Handle modal open/close and document changes
  useEffect(() => {
    if (isOpen && documentApprovalId) {
      // Clean up any previous state
      cleanup();
      
      // Always start with review step - no complex workflow
      setWorkflowStep('review');
      
      // Reset webhook tracking when modal opens
      setWebhookCalled(false);
      
      // Reset loading states
      setLoading(true);
      setDocumentError(null);
      
      // Always fetch document data for preview, but use initial OCR data if available
      if (initialOcrData && Object.keys(initialOcrData).length > 0) {
        setOcrData(normalizeOcrData(initialOcrData));
        setLoading(false);
      }
      
      // Always fetch document data for preview (file_url, etc.)
      fetchDocumentData();
    } else if (!isOpen) {
      // Clean up when modal closes
      cleanup();
      // Reset webhook tracking when modal closes
      setWebhookCalled(false);
      setBankNameOtherMode(false);
    }
    
    // Cleanup on unmount or when documentApprovalId/documentType changes
    return cleanup;
  }, [isOpen, documentApprovalId, documentType, mode]); // Include all dependencies

  // Separate effect to load document blob when documentData changes
  useEffect(() => {
    const fileUrl = documentData?.document_info?.file_url;
    
    if (isOpen && fileUrl && documentData && fileUrl !== currentFileUrlRef.current && !loadingDocument) {
      loadDocumentBlob(fileUrl);
    }
  }, [documentData?.document_info?.file_url, isOpen, documentData]);

  // Re-run role detection when OCR data changes
  useEffect(() => {
    if (mode !== 'hr' && documentType === 'marriage_certificate' && Object.keys(ocrData).length > 0) {
      console.log('🔍 OCR data changed, re-running role detection');
      // Force re-render by updating a dummy state
      setSelectedRole(null); // Reset selected role to trigger re-detection
    }
  }, [ocrData, documentType, mode]);

  // Live BCA scheme detection (HR review of qualification certificates only).
  // Debounced so HR gets a "Detected: SEC(K)" chip that follows their edits to
  // the certificate name/short name without a request per keystroke.
  useEffect(() => {
    const isQualificationType =
      documentType === 'professional_certificate' || documentType === 'education_certificate';
    if (mode !== 'hr' || !isQualificationType) {
      setDetectedScheme(null);
      return undefined;
    }

    const certName = (ocrData.cert_name || ocrData.education_qualification_name || '').trim();
    const certShortName = (ocrData.cert_short_name || '').trim();
    if (!certName && !certShortName) {
      setDetectedScheme(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const response = await confidentialAPI.identifyCertificateScheme({
          cert_short_name: certShortName,
          cert_name: certName,
        });
        if (!cancelled) {
          setDetectedScheme(response.data?.scheme || null);
        }
      } catch (error) {
        if (!cancelled) {
          setDetectedScheme(null);
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ocrData.cert_name, ocrData.cert_short_name, ocrData.education_qualification_name, documentType, mode]);

  // Conflicting identity values require an explicit HR choice.
  useEffect(() => {
    if (mode === 'hr' && existingProfileData && Object.keys(ocrData).length > 0) {
      const conflicts = detectConflicts(ocrData, existingProfileData);
      if (Object.keys(conflicts).length === 0) {
        // Clear selected values if no conflicts
        setSelectedValues({});
      }
    }
  }, [ocrData, existingProfileData, mode]);

  // Simple role selection handler
  const handleRoleSelection = (role) => {
    setSelectedRole(role);
  };

  const fetchDocumentData = async ({ silent = false } = {}) => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      if (!silent) setLoading(true);
      setDocumentError(null);
      
      // Check if document type is supported
      const supportedTypes = [
        'marriage_certificate', 
        'birth_certificate',
        'interim_birth_notification',
        'id_card_front',
        'id_card_back', 
        'passport',
        'education_certificate',
        'professional_certificate',
        'professional_card',
        'medical_certificate',
        'bank_statement',
        'death_certificate',
        'driving_license'
      ];
      
      if (!supportedTypes.includes(documentType)) {
        throw new Error(`Document verification not supported for type: ${documentType}`);
      }
      
      const endpoints = getApiEndpoints();

      let data;
      if (documentType === 'medical_certificate') {
        // For MC, we must use applicationId; prefer the prop passed from parent, then fall back to cached data
        const applicationId = applicationIdProp || (documentData?.application_id) || initialOcrData?.application_id || null;
        if (!applicationId) {
          throw new Error('Missing application_id for medical certificate verification');
        }
        const response = await api.get(`/mc/verification-data/${applicationId}/`, {
          signal: abortController.signal
        });

        const extractedData = response.data?.extracted_data || {};
        const normalizedMcOcr = {
          ocr_patient_full_name: extractedData.ocr_patient_full_name || extractedData.full_name,
          ocr_patient_nric_fin: extractedData.ocr_patient_nric_fin || extractedData.nric_fin,
          ocr_mc_number: extractedData.ocr_mc_number || extractedData.mc_number,
          ocr_clinic_hospital_name: extractedData.ocr_clinic_hospital_name || extractedData.clinic_hospital_name,
          ocr_doctor_name: extractedData.ocr_doctor_name || extractedData.doctor_name,
          ocr_diagnosis: extractedData.ocr_diagnosis || extractedData.diagnosis,
          ocr_mc_issue_date: extractedData.ocr_mc_issue_date || extractedData.issue_date,
          ocr_mc_start_date: extractedData.ocr_mc_start_date || extractedData.start_date,
          ocr_mc_end_date: extractedData.ocr_mc_end_date || extractedData.end_date,
          ocr_mc_total_days: extractedData.ocr_mc_total_days || extractedData.total_days || extractedData.mc_total_days,
        };

        const documentPath = response.data?.mc_file_path || response.data?.document_file || null;
        data = {
          document_info: {
            status: response.data?.workflow?.status || 'pending',
            filename: documentPath ? documentPath.split('/').pop() : 'medical_certificate.pdf',
            file_url: documentPath ? `/api/v1/documents/view/${documentPath}` : null,
          },
          ocr_data: normalizedMcOcr,
          employee_data: response.data?.employee_data || {},
          application_id: applicationId,
          status: response.data?.workflow?.status || 'pending'
        };
      } else if (documentType === 'interim_birth_notification') {
        const response = await api.get(endpoints.verification, {
          signal: abortController.signal
        });
        data = response.data;
        // Pre-fill all form fields from backend prefill block (includes spouse name and
        // any previously saved values when reopening an already-verified record)
        const pf = data?.prefill || {};
        setInterimForm(prev => ({
          ...prev,
          spouse_name:                  pf.spouse_name                  || prev.spouse_name,
          child_date_of_birth:          pf.child_date_of_birth          || prev.child_date_of_birth,
          hospital:                     pf.hospital                     || prev.hospital,
          child_citizenship_at_birth:   pf.child_citizenship_at_birth   || prev.child_citizenship_at_birth,
          source_document_type:         pf.source_document_type         || prev.source_document_type,
          source_document_type_other:   pf.source_document_type_other   || prev.source_document_type_other,
          hr_notes:                     pf.hr_notes                     || prev.hr_notes,
        }));
      } else {
        const response = await api.get(endpoints.verification, {
          signal: abortController.signal
        });
        data = response.data;
      }
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setDocumentData(data);

      // Driving licence: hydrate the multi-class working array from the response
      if (documentType === 'driving_license') {
        const rawClasses = (Array.isArray(data?.driving_licenses) && data.driving_licenses)
          || (Array.isArray(data?.ocr_data?.driving_licenses) && data.ocr_data.driving_licenses)
          || [];
        setDrivingLicenseClasses(rawClasses.map(normalizeDrivingLicenseRow));
      }

      // Sync workflow step from server status
      const serverStatus = (data?.document_info?.status || data?.status || data?.approval_status || data?.verification_status || data?.document_status || data?.current_status || '').toString().toLowerCase();
      if (serverStatus === 'submitted') setWorkflowStep('submitted');
      if (['verified','approved','validated'].includes(serverStatus)) setWorkflowStep('verified');
      
      // Latest persisted OCR wins. Parent `initialOcrData` is only a first-paint
      // hint (same-session upload). Spreading it over the GET used to freeze the
      // Review form on a stale client snapshot after a later persist / re-analyse.
      const serverOcr = data.ocr_data || {};
      const normalizedServerOcr = normalizeOcrData(serverOcr);
      const normalizedInitialOcr = normalizeOcrData(initialOcrData);
      const usesDedicatedOcrState = ['driving_license', 'interim_birth_notification'].includes(documentType);
      const formFieldKeys = usesDedicatedOcrState
        ? []
        : getFieldConfig().map((field) => field.key);
      const serverHasOcr = hasMeaningfulDocumentOcrData(normalizedServerOcr, formFieldKeys);
      const hasMeaningfulInitialOcr = hasMeaningfulDocumentOcrData(normalizedInitialOcr, formFieldKeys);

      if (serverHasOcr) {
        setOcrData(normalizedServerOcr);
      } else if (!silent && hasMeaningfulInitialOcr) {
        setOcrData(normalizedInitialOcr);
      } else if (!silent && initialOcrData && Object.keys(initialOcrData).length > 0) {
        setOcrData(normalizedInitialOcr);
      } else if (!silent) {
        setOcrData({});
      }
      
      if (data.existing_profile_data && mode === 'hr') {
        setExistingProfileData(data.existing_profile_data);
      } else {
        setExistingProfileData(null);
      }
      
      // Check again if request was aborted before setting state
      if (abortController.signal.aborted) {
        return;
      }
      
      setDocumentData(data);

      // Note: Document blob loading is handled by separate useEffect
      // This ensures proper cleanup and avoids race conditions
      return serverHasOcr ? normalizedServerOcr : null;
    } catch (error) {
      // Don't show error if request was aborted/cancelled
      if (error.name === 'AbortError' || error.name === 'CanceledError' || abortController.signal.aborted) {
        return;
      }
      
      console.error('Error fetching document data:', error);
      if (silent) return;
      setDocumentError(error.message || 'Failed to load document data');
      
      if (error.message?.includes('not supported')) {
        toast.error(error.message);
        onClose(); // Close modal for unsupported document types
      } else {
        toast.error('Failed to load document data');
      }
    } finally {
      // Only update loading state if request wasn't aborted
      if (!silent && !abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Re-read the stored file. No re-upload. Does not change an approved or
  // rejected document — the button is hidden once the review is read-only.
  const handleRerunOcr = async () => {
    if (rerunningOcr) return;
    const applicationId = applicationIdProp || documentData?.application_id || null;
    const formData = new FormData();
    let endpoint = '';
    let timeout = 120000;

    if (documentType === 'medical_certificate') {
      if (!applicationId) return;
      formData.append('application_id', applicationId);
      endpoint = '/medical-certificate/trigger-ocr/';
      timeout = 90000;
    } else if (!documentApprovalId) {
      return;
    } else if (documentType === 'passport') {
      formData.append('document_approval_id', documentApprovalId);
      endpoint = '/passport/trigger-ocr/';
      timeout = 90000;
    } else if (documentType === 'education_certificate' || documentType === 'professional_certificate') {
      formData.append('document_approval_id', documentApprovalId);
      formData.append('document_type', documentType);
      endpoint = '/qualification/trigger-ocr/';
      timeout = 200000;
    } else if (documentType === 'id_card_front' || documentType === 'id_card_back') {
      formData.append('document_approval_id', documentApprovalId);
      formData.append('card_side', documentType === 'id_card_front' ? 'front' : 'back');
      endpoint = '/id-card/trigger-ocr/';
      timeout = 90000;
    } else {
      const endpointsByType = {
        birth_certificate: '/child-certificate/trigger-ocr/',
        marriage_certificate: '/marriage-certificate/trigger-ocr/',
        death_certificate: '/death-certificate/trigger-ocr/',
        driving_license: '/driving-license/trigger-ocr/',
        bank_statement: '/bank-statement/trigger-ocr/',
      };
      endpoint = endpointsByType[documentType];
      if (!endpoint) return;
      formData.append('document_approval_id', documentApprovalId);
      timeout = 200000;
    }

    setRerunningOcr(true);
    const loadingToast = toast.loading('Extracting details…');
    try {
      const resp = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout,
      });
      toast.dismiss(loadingToast);
      const payload = resp?.data || {};

      if (!payload.success) {
        const hrMessage =
          payload.message
          || payload.error
          || 'Could not read this document. Please fill in the fields from the scan.';
        toast.warning(hrMessage, { duration: 6000 });
        return;
      }

      const refreshed = await fetchDocumentData({ silent: true });
      if (refreshed) {
        toast.success('Extracted details updated');
      } else {
        toast.warning(
          'Could not read this document. Please fill in the fields from the scan.',
          { duration: 6000 },
        );
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('OCR re-run failed:', error);
      const apiMessage =
        error?.response?.data?.message
        || error?.response?.data?.error
        || 'Failed to analyse document. Please try again.';
      toast.error(apiMessage);
    } finally {
      setRerunningOcr(false);
    }
  };

  // Empty extracted fields are not a resting state. One auto-attempt per
  // passport id — after a miss, the banner's Extract again is the retry.
  useEffect(() => {
    if (!isOpen || loading || rerunningOcr) return;
    if (documentType !== 'passport' || !documentApprovalId) return;
    if (workflowStep !== 'review') return;

    const status = (
      documentData?.document_info?.status || documentData?.status || ''
    ).toString().toLowerCase();
    if (['verified', 'approved', 'validated', 'rejected'].includes(status)) return;

    const passportKeys = [
      'ocr_passport_first_name',
      'ocr_passport_last_name',
      'ocr_passport_number',
      'ocr_passport_nationality',
      'ocr_passport_date_of_birth',
      'ocr_passport_gender',
      'ocr_passport_expiry_date',
    ];
    if (hasMeaningfulDocumentOcrData(ocrData, passportKeys)) return;
    if (autoPassportOcrAttemptedRef.current === documentApprovalId) return;

    autoPassportOcrAttemptedRef.current = documentApprovalId;
    handleRerunOcr();
    // handleRerunOcr is recreated each render; the ref is the one-shot guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    loading,
    rerunningOcr,
    documentType,
    documentApprovalId,
    workflowStep,
    documentData,
    ocrData,
  ]);

  const loadDocumentBlob = async (fileUrl) => {
    if (blobAbortControllerRef.current) {
      blobAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    blobAbortControllerRef.current = abortController;
    
    try {
      setLoadingDocument(true);
      setDocumentError(null);

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (!fileUrl) {
        throw new Error('Missing document URL');
      }

      let apiPath = fileUrl;
      if (fileUrl.includes('/view/')) {
        const urlParts = fileUrl.split('/view/');
        apiPath = urlParts[urlParts.length - 1];
      } else if (fileUrl.startsWith('/documents/view/')) {
        apiPath = fileUrl.replace('/documents/view/', '');
      }
      apiPath = apiPath.replace(/\/+$/, '');
      
      const response = await api.get(`/documents/view/${encodeURIComponent(apiPath)}/`, { 
        responseType: 'blob',
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      const fileExt = (apiPath.split('.').pop() || '').toLowerCase();
      const inferredType = inferContentType(fileExt, response.headers?.['content-type']);
      let blob = new Blob([response.data], { type: inferredType });

      if (isHeicFile(apiPath)) {
        blob = await convertHeicBlobToJpeg(blob);
      }

      const blobUrl = URL.createObjectURL(blob);
      
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = blobUrl;
      setDocumentBlobUrl(blobUrl);
      currentFileUrlRef.current = fileUrl;
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || abortController.signal.aborted) {
        return;
      }
      console.error('Error loading document:', error);
      setDocumentError('Failed to load document for preview');
    } finally {
      setLoadingDocument(false);
    }
  };

  // Helper function to normalize select options (supports both string array and object array formats)
  const normalizeOption = (option) => {
    if (typeof option === 'string') {
      // Backward compatible: simple string array
      return { value: option, label: option };
    }
    // New format: object with value and label
    return option;
  };

  // Helper function to determine if a field should be visible based on ID Card Type
  const isFieldVisible = (field, ocrData) => {
    // Only apply conditional visibility for ID card front (not back)
    if (documentType !== 'id_card_front') {
      return true;
    }

    const idCardType = (ocrData.id_card_type || '').toLowerCase();
    const wpTypes = ['wp', 'work permit'];
    const citizenPrTypes = ['citizen', 'pr'];
    const epTypes = ['ep', 'employment pass'];
    const sPassTypes = ['s_pass', 's pass'];
    const compactNricFin = String(
      ocrData.id_card_nric_fin_number || ''
    ).replace(/\s+/g, '').toUpperCase();
    const hasGenuineFin = /^[FGM]\d{7}[A-Z]$/.test(compactNricFin);
    
    // For Work Permit holders: only show Full Name, Work Permit Number, and ID Card Type
    if (wpTypes.includes(idCardType)) {
      const allowedFields = [
        'id_card_name',
        'id_card_wp_number',
        'id_card_type',
        'card_serial_number',
      ];
      return allowedFields.includes(field.key);
    }
    
    // For Citizen/PR holders: only show essential identity fields
    if (citizenPrTypes.includes(idCardType)) {
      const allowedFields = [
        'id_card_name',
        'id_card_nric_fin_number',
        'id_card_dob',
        'id_card_sex',
        'id_card_nationality',
        'id_card_race',
        'id_card_type'
      ];
      return allowedFields.includes(field.key);
    }
    
    // S Pass No. is distinct from FIN. Only show FIN when the document
    // genuinely supplied a separately labelled FIN.
    if (sPassTypes.includes(idCardType)) {
      const allowedFields = [
        'id_card_name',
        'id_card_type',
        'id_card_wp_number',
        'card_serial_number',
      ];
      if (hasGenuineFin) {
        allowedFields.push('id_card_nric_fin_number');
      }
      return allowedFields.includes(field.key);
    }

    // Employment Pass cards use FIN as their primary visible identifier.
    if (epTypes.includes(idCardType)) {
      const allowedFields = [
        'id_card_name',
        'id_card_type',
        'id_card_nric_fin_number',
        'card_serial_number',
      ];
      return allowedFields.includes(field.key);
    }
    
    // Default: show all fields (for empty/undefined type)
    return true;
  };

  const handleOcrDataChange = (field, value) => {
    isDirtyRef.current = true;
    const newData = {
      ...ocrData,
      [field]: value
    };
    
    // Clear fields when ID Card Type changes (only for ID card front)
    if (field === 'id_card_type' && documentType === 'id_card_front') {
      const wpTypes = ['wp', 'work permit'];
      const citizenPrTypes = ['citizen', 'pr'];
      const epTypes = ['ep', 'employment pass'];
      const sPassTypes = ['s_pass', 's pass'];
      const valueLower = value?.toLowerCase();
      
      // For WP type: keep only fields represented by a Work Permit front.
      if (wpTypes.includes(valueLower)) {
        const allowedFields = [
          'id_card_name',
          'id_card_wp_number',
          'id_card_type',
          'card_serial_number',
        ];
        const fieldsToClear = [
          'id_card_nric_fin_number', 'id_card_dob', 'id_card_sex', 'id_card_nationality', 
          'id_card_race', 'id_card_issue_date',
          'occupation', 'date_of_application', 'date_of_expiry',
          'id_card_color', 'employer_name', 'sector', 'card_serial_number'
        ];
        fieldsToClear.forEach(fieldKey => {
          if (!allowedFields.includes(fieldKey)) {
            newData[fieldKey] = '';
          }
        });
      }
      
      // For Citizen/PR type: clear fields not in the allowed list
      if (citizenPrTypes.includes(valueLower)) {
        const allowedFields = [
          'id_card_name', 'id_card_nric_fin_number', 'id_card_dob',
          'id_card_sex', 'id_card_nationality', 'id_card_race', 'id_card_type'
        ];
        const fieldsToClear = [
          'id_card_wp_number', 'id_card_issue_date',
          'occupation', 'date_of_application', 'date_of_expiry',
          'id_card_color', 'employer_name', 'sector', 'card_serial_number'
        ];
        fieldsToClear.forEach(fieldKey => {
          if (!allowedFields.includes(fieldKey)) {
            newData[fieldKey] = '';
          }
        });
      }
      
      // S Pass No. maps to the shared work-pass-number profile field.
      if (sPassTypes.includes(valueLower)) {
        const allowedFields = [
          'id_card_name',
          'id_card_type',
          'id_card_wp_number',
          'card_serial_number',
        ];
        const fieldsToClear = [
          'id_card_nric_fin_number', 'id_card_dob', 'id_card_sex', 'id_card_nationality',
          'id_card_race', 'id_card_issue_date',
          'occupation', 'date_of_application', 'date_of_expiry',
          'id_card_color', 'employer_name', 'sector'
        ];
        fieldsToClear.forEach(fieldKey => {
          if (!allowedFields.includes(fieldKey)) {
            newData[fieldKey] = '';
          }
        });
      }

      // Employment Pass uses FIN as the visible identifier.
      if (epTypes.includes(valueLower)) {
        const allowedFields = [
          'id_card_name',
          'id_card_type',
          'id_card_nric_fin_number',
          'card_serial_number',
        ];
        const fieldsToClear = [
          'id_card_wp_number', 'id_card_dob', 'id_card_sex',
          'id_card_nationality', 'id_card_race', 'id_card_issue_date',
          'occupation', 'date_of_application', 'date_of_expiry',
          'id_card_color', 'employer_name', 'sector'
        ];
        fieldsToClear.forEach(fieldKey => {
          if (!allowedFields.includes(fieldKey)) {
            newData[fieldKey] = '';
          }
        });
      }
    }
    
    setOcrData(newData);
  };

  // Marriage certificate workflow handlers
  const handleSubmitForReview = async () => {
    // Validate required fields
    const fields = getFieldConfig(documentType);
    const missingFields = [];

    for (const field of fields) {
      // Check if field is conditionally required (e.g., field_of_study for higher education)
      let isRequired = field.required;
      if (field.conditionallyRequired && documentType === 'education_certificate') {
        if (field.key === 'field_of_study') {
          isRequired = isFieldOfStudyRequired(ocrData);
        }
      }
      
      // For ID card front, only validate required fields that are visible
      // For other document types, validate all required fields
      const shouldValidate = documentType === 'id_card_front' 
        ? (isRequired && isFieldVisible(field, ocrData))
        : isRequired;
      
      if (shouldValidate) {
        const value = ocrData[field.key];
        // For number fields, check if value is null, undefined, or empty string
        if (field.type === 'number') {
          if (value === null || value === undefined || value === '' || isNaN(value)) {
            missingFields.push(field.label);
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          missingFields.push(field.label);
        }
      }
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      
      // For marriage certificates, call webhook first if role is present and webhook hasn't been called
      if (documentType === 'marriage_certificate' && employeeRole && !webhookCalled && mode !== 'hr') {
        try {
          // Get staff_id from employeeProfile
          const staffId = employeeProfile?.staff_id || employeeProfile?.id || null;
          if (!staffId) {
            console.error('⚠️ Staff ID not available for webhook call');
            toast.error('Unable to process marriage certificate. Please refresh and try again.');
            setSaving(false);
            return;
          }

          // Prepare OCR data for webhook (use keys that backend expects)
          const webhookOcrData = {
            bridegroom_full_name: ocrData.bridegroom_full_name || ocrData.ocr_bridegroom_full_name || '',
            bride_full_name: ocrData.bride_full_name || ocrData.ocr_bride_full_name || '',
            marriage_date: ocrData.marriage_date || ocrData.ocr_marriage_date || '',
            marriage_certificate_number: ocrData.marriage_certificate_number || ocrData.ocr_marriage_certificate_number || '',
            marriage_venue: ocrData.marriage_venue || ocrData.ocr_marriage_venue || '',
            witness_1_name: ocrData.witness_1_name || ocrData.ocr_witness_1_name || '',
            witness_2_name: ocrData.witness_2_name || ocrData.ocr_witness_2_name || '',
            confidence_score: ocrData.confidence_score || ocrData.ocr_confidence_score || 95.0
          };

          console.log('[Marriage Submit] Calling webhook before submission:', {
            document_approval_id: documentApprovalId,
            staff_id: staffId,
            employee_role: employeeRole,
            ocr_data: webhookOcrData
          });

          await api.post('/marriage-certificate/ocr-webhook/', {
            document_approval_id: documentApprovalId,
            staff_id: staffId,
            employee_role: employeeRole,
            ocr_data: webhookOcrData
          });

          console.log('✅ Marriage certificate webhook called successfully');
          setWebhookCalled(true);
        } catch (webhookError) {
          console.error('⚠️ Failed to call marriage certificate webhook:', webhookError);
          toast.error('Failed to process marriage certificate data. Please try again.');
          setSaving(false);
          return;
        }
      }
      
      // Use the correct endpoint based on mode
      const endpoints = getApiEndpoints();
      const endpoint = mode === 'hr' ? endpoints.verify : endpoints.submitAfterReview;
      
      // Prepare payload based on mode
      const payload = mode === 'hr' 
        ? {
            document_approval_id: documentApprovalId,
            action: 'approve' // or 'reject' based on HR decision
          }
        : {
            document_approval_id: documentApprovalId,
            amended_ocr_data: ocrData,
            employee_role: employeeRole
          };
      
      await api.post(endpoint, payload);
      
      setWorkflowStep('submitted');
      toast.success('✅ Document submitted for HR review!');
      
      // Update parent component if callback provided
      if (onApprovalComplete) {
        onApprovalComplete({ status: 'submitted', role: employeeRole });
      }
      wasFinalisedRef.current = true;
    } catch (error) {
      console.error('Error submitting document:', error);
      toast.error('Failed to submit document for review');
    } finally {
      setSaving(false);
    }
  };



  // Helper function to clean OCR data - remove empty/null values and filter by visibility
  const cleanOcrData = (data, docType) => {
    const cleaned = {};
    const fields = getFieldConfig(docType);
    
    for (const field of fields) {
      const value = data[field.key];
      
      // For ID card front, only include visible fields
      if (docType === 'id_card_front') {
        if (!isFieldVisible(field, data)) {
          continue; // Skip hidden fields
        }
      }
      
      // Only include fields with actual values (not null, undefined, or empty strings)
      if (value !== null && value !== undefined && value !== '') {
        // For strings, also check if trimmed value is not empty
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed !== '') {
            cleaned[field.key] = trimmed;
          }
        } else {
          // For non-string values (dates, numbers, etc.), include as-is
          cleaned[field.key] = value;
        }
      }
    }
    
    return cleaned;
  };

  // Build the amended OCR payload for the HR verify persist step.
  // Unlike cleanOcrData, this PRESERVES fields the HR user explicitly cleared
  // (sent as '') so the backend overwrites the stale raw OCR value instead of
  // silently keeping it. Fields never present in ocrData (undefined/null) are
  // omitted so we never wipe data the form never controlled.
  const buildAmendedOcrData = (data, docType) => {
    const amended = {};
    const fields = getFieldConfig(docType);

    for (const field of fields) {
      // For ID card front, only include visible fields (mirror cleanOcrData)
      if (docType === 'id_card_front' && !isFieldVisible(field, data)) {
        continue;
      }

      const value = data[field.key];
      if (value === null || value === undefined) {
        continue; // never controlled by the form — don't overwrite
      }
      amended[field.key] = typeof value === 'string' ? value.trim() : value;
    }

    return amended;
  };

  const handleSaveOcrData = async () => {
    if (documentType === 'driving_license') {
      return handleDrivingLicenseEmployeeSubmit();
    }
    // Validate required fields
    const fields = getFieldConfig(documentType);
    const missingFields = [];

    for (const field of fields) {
      // Check if field is conditionally required (e.g., field_of_study for higher education)
      let isRequired = field.required;
      if (field.conditionallyRequired && documentType === 'education_certificate') {
        if (field.key === 'field_of_study') {
          isRequired = isFieldOfStudyRequired(ocrData);
        }
      }
      
      // For ID card front, only validate required fields that are visible
      // For other document types, validate all required fields
      const shouldValidate = documentType === 'id_card_front' 
        ? (isRequired && isFieldVisible(field, ocrData))
        : isRequired;
      
      if (shouldValidate) {
        const value = ocrData[field.key];
        // For number fields, check if value is null, undefined, or empty string
        if (field.type === 'number') {
          if (value === null || value === undefined || value === '' || isNaN(value)) {
            missingFields.push(field.label);
          }
        } else if (!value || (typeof value === 'string' && value.trim() === '')) {
          missingFields.push(field.label);
        }
      }
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      const endpoints = getApiEndpoints();
      
      // Use the correct endpoint based on document type and mode
      // Employees submit after review; HR should persist edits via edit-ocr
      const isIdCard = documentType === 'id_card_front' || documentType === 'id_card_back';
      const isQualificationType = documentType === 'education_certificate' || documentType === 'professional_certificate';
      const isBankStatement = documentType === 'bank_statement';
      const isPassportType = documentType === 'passport';
      const endpoint = (documentType === 'birth_certificate' || isIdCard || isBankStatement || isPassportType)
        ? (mode === 'hr' ? endpoints.editOcr : endpoints.submitAfterReview)
        : isQualificationType
        ? (mode === 'hr' ? endpoints.editOcr : endpoints.submitAfterReview)
        : endpoints.editOcr;
      
      // Clean OCR data - remove empty values and filter by visibility
      const cleanedOcrData = cleanOcrData(ocrData, documentType);
      
      // Prepare payload based on document type
      const payload = (documentType === 'birth_certificate' || isIdCard || isBankStatement || isPassportType)
        ? {
            document_approval_id: documentApprovalId,
            amended_ocr_data: cleanedOcrData
          }
        : isQualificationType
        ? {
            document_approval_id: documentApprovalId,
            amended_ocr_data: cleanedOcrData
          }
        : {
            document_approval_id: documentApprovalId,
            ocr_data: cleanedOcrData
          };
      
      await api.post(endpoint, payload);
      
      // Update document status in state to reflect submission
      if (documentData) {
        setDocumentData(prev => ({
          ...prev,
          document_info: {
            ...prev.document_info,
            status: 'submitted'
          },
          status: 'submitted'
        }));
      }
      setWorkflowStep('submitted');
      
      // Show success message based on document type
      const getSuccessMessage = (docType) => {
        switch (docType) {
          case 'id_card_front':
          case 'id_card_back':
            return '✅ ID card submitted successfully! It will be reviewed by HR.';
          case 'education_certificate':
            return '✅ Education certificate submitted successfully! It will be reviewed by HR.';
          case 'professional_certificate':
            return '✅ Professional certificate submitted successfully! It will be reviewed by HR.';
          case 'birth_certificate':
            return '✅ Birth certificate submitted successfully! It will be reviewed by HR.';
          case 'marriage_certificate':
            return '✅ Marriage certificate submitted successfully! It will be reviewed by HR.';
          case 'bank_statement':
            return '✅ Bank statement submitted successfully! It will be reviewed by HR.';
          case 'passport':
            return '✅ Passport submitted successfully! It will be reviewed by HR.';
          default:
            return '✅ Document submitted successfully! It will be reviewed by HR.';
        }
      };
      const successMessage = getSuccessMessage(documentType);
      toast.success(successMessage);
      
      // Update parent component if callback provided
      if (onApprovalComplete) {
        onApprovalComplete({ status: 'submitted' });
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error saving OCR data:', error);
      // Show error message based on document type
      const getErrorMessage = (docType) => {
        switch (docType) {
          case 'id_card_front':
          case 'id_card_back':
            return 'Failed to submit ID card. Please try again.';
          case 'education_certificate':
            return 'Failed to submit education certificate. Please try again.';
          case 'professional_certificate':
            return 'Failed to submit professional certificate. Please try again.';
          case 'birth_certificate':
            return 'Failed to submit birth certificate. Please try again.';
          case 'marriage_certificate':
            return 'Failed to submit marriage certificate. Please try again.';
          case 'bank_statement':
            return 'Failed to submit bank statement. Please try again.';
          case 'passport':
            return 'Failed to submit passport. Please try again.';
          default:
            return 'Failed to submit document. Please try again.';
        }
      };
      // Surface the real backend reason (e.g. "Document already submitted for
      // review") instead of always showing the generic message. This turns a
      // silent dead-end into an actionable error for the user and HR.
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.detail;
      const baseMessage = getErrorMessage(documentType);
      toast.error(backendError ? `${baseMessage} (${backendError})` : baseMessage);
    } finally {
      setSaving(false);
    }
  };

  // ── Driving Licence (multi-class) handlers ──────────────────────────────
  const dlUpdateRow = (idx, patch) =>
    setDrivingLicenseClasses(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const dlAddRow = () =>
    setDrivingLicenseClasses(prev => ([
      ...prev,
      { license_class: '', issue_date: '', expiry_date: '', license_number: '', notes: '', is_lifetime: false },
    ]));
  const dlRemoveRow = (idx) =>
    setDrivingLicenseClasses(prev => prev.filter((_, i) => i !== idx));

  const dlSanitizeForSubmit = () => drivingLicenseClasses
    .map(r => {
      const isLifetime = r.is_lifetime || !((r.expiry_date || '').toString().trim());
      return {
        license_class: (r.license_class || '').toString().trim().toLowerCase(),
        issue_date: (r.issue_date || '').toString().trim() || null,
        expiry_date: isLifetime ? null : ((r.expiry_date || '').toString().trim() || null),
        license_number: (r.license_number || '').toString().trim() || null,
        notes: (r.notes || '').toString().trim() || null,
        is_lifetime: isLifetime,
      };
    })
    .filter(r => r.license_class);

  const dlValidate = () => {
    const entries = dlSanitizeForSubmit();
    if (entries.length === 0) {
      toast.error('Add at least one licence class.');
      return null;
    }
    const seen = new Set();
    for (const e of entries) {
      if (seen.has(e.license_class)) {
        toast.error(`Duplicate licence class: ${e.license_class.toUpperCase()}`);
        return null;
      }
      seen.add(e.license_class);
      if (!e.issue_date) {
        toast.error(`Issue date is required for Class ${e.license_class.toUpperCase()}.`);
        return null;
      }
    }
    return entries;
  };

  const handleDrivingLicenseEmployeeSubmit = async () => {
    const entries = dlValidate();
    if (!entries) return;
    try {
      setSaving(true);
      const endpoints = getApiEndpoints();
      await api.post(endpoints.submitAfterReview, {
        document_approval_id: documentApprovalId,
        amended_ocr_data: { driving_licenses: entries },
      });
      setWorkflowStep('submitted');
      toast.success('✅ Driving licence submitted for HR verification!');
      if (onApprovalComplete) onApprovalComplete({ status: 'submitted' });
      wasFinalisedRef.current = true;
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Error submitting driving licence:', error);
      toast.error(error?.response?.data?.error || 'Failed to submit driving licence');
    } finally {
      setSaving(false);
    }
  };

  const handleDrivingLicenseHrAction = async (action) => {
    try {
      setSaving(true);
      const endpoints = getApiEndpoints();
      if (action === 'approve') {
        const entries = dlValidate();
        if (!entries) { setSaving(false); return; }
        // Persist HR amendments before verifying so the server materialises the
        // edited classes (not stale OCR rows) into DrivingLicense records.
        await api.post(endpoints.editOcr, {
          document_approval_id: documentApprovalId,
          amended_ocr_data: { driving_licenses: entries },
        });
        const resp = await api.post(endpoints.verify, {
          document_approval_id: documentApprovalId,
          action: 'approve',
        });
        if (onApprovalComplete) onApprovalComplete(resp.data);
        wasFinalisedRef.current = true;
        onClose();
      } else {
        const resp = await api.post(endpoints.verify, {
          document_approval_id: documentApprovalId,
          action: 'reject',
          rejection_reason: rejectionReason.trim(),
          notes: rejectionReason.trim(),
        });
        if (onApprovalComplete) onApprovalComplete(resp.data);
        wasFinalisedRef.current = true;
        onClose();
      }
    } catch (error) {
      console.error('Error processing driving licence approval:', error);
      toast.error(error?.response?.data?.error || `Failed to ${action} driving licence`);
    } finally {
      setSaving(false);
    }
  };

  const renderDrivingLicenseSection = () => {
    const classes = drivingLicenseClasses;
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Each licence class is grouped with its own issue date. Review and amend below.
          </p>
          {!isReadOnly && (
            <button
              type="button"
              onClick={dlAddRow}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add class
            </button>
          )}
        </div>

        {classes.length === 0 && (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
            <CreditCard className="w-6 h-6 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No licence classes detected.</p>
            {!isReadOnly && (
              <button
                type="button"
                onClick={dlAddRow}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add a class
              </button>
            )}
          </div>
        )}

        {classes.map((row, index) => {
          const classMeta = DRIVING_LICENSE_CLASS_OPTIONS.find(o => o.value === row.license_class);
          const classLabel = classMeta ? classMeta.label : (row.license_class ? `Class ${row.license_class.toUpperCase()}` : 'New class');
          return (
            <div key={index} className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {classLabel}
                </h4>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => dlRemoveRow(index)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove this class"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Licence Class <span className="text-red-500">*</span>
                  </label>
                  <FormSelect
                    theme={resolvedTheme}
                    value={row.license_class}
                    onChange={(val) => dlUpdateRow(index, { license_class: val })}
                    disabled={isReadOnly}
                    options={DRIVING_LICENSE_CLASS_OPTIONS}
                    placeholder="Select class…"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <Calendar className="w-3.5 h-3.5 inline mr-1" />
                    Issue Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={row.issue_date || ''}
                    onChange={(e) => dlUpdateRow(index, { issue_date: e.target.value })}
                    disabled={isReadOnly}
                    className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-600 mb-1">
                    <input
                      type="checkbox"
                      checked={!!row.is_lifetime}
                      onChange={(e) => dlUpdateRow(index, {
                        is_lifetime: e.target.checked,
                        expiry_date: e.target.checked ? '' : row.expiry_date,
                      })}
                      disabled={isReadOnly}
                      className="rounded border-gray-300"
                    />
                    <InfinityIcon className="w-3.5 h-3.5" />
                    Lifetime (no expiry)
                  </label>
                  {!row.is_lifetime && (
                    <div className="mt-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Calendar className="w-3.5 h-3.5 inline mr-1" />
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        value={row.expiry_date || ''}
                        onChange={(e) => dlUpdateRow(index, { expiry_date: e.target.value })}
                        disabled={isReadOnly}
                        className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <FileText className="w-3.5 h-3.5 inline mr-1" />
                    Licence Number
                  </label>
                  <input
                    type="text"
                    value={row.license_number || ''}
                    onChange={(e) => dlUpdateRow(index, { license_number: e.target.value })}
                    disabled={isReadOnly}
                    placeholder="If available"
                    className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                  <input
                    type="text"
                    value={row.notes || ''}
                    onChange={(e) => dlUpdateRow(index, { notes: e.target.value })}
                    disabled={isReadOnly}
                    className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleApprovalAction = async (action) => {
    if (documentType === 'driving_license') {
      return handleDrivingLicenseHrAction(action);
    }
    try {
      setSaving(true);

      // Validate required fields for approval
      if (action === 'approve') {
        const fields = getFieldConfig(documentType);
        const missingFields = [];

        for (const field of fields) {
          // Check if field is conditionally required (e.g., field_of_study for higher education)
          let isRequired = field.required;
          if (field.conditionallyRequired && documentType === 'education_certificate') {
            if (field.key === 'field_of_study') {
              isRequired = isFieldOfStudyRequired(ocrData);
            }
          }

          // For ID card front, only validate required fields that are visible for the
          // selected ID Card Type (e.g. Date of Birth/Gender are hidden for WP/EP/S Pass)
          const shouldValidate = documentType === 'id_card_front'
            ? (isRequired && isFieldVisible(field, ocrData))
            : isRequired;

          if (shouldValidate) {
            const value = ocrData[field.key];
            // For number fields, check if value is null, undefined, or empty string
            if (field.type === 'number') {
              if (value === null || value === undefined || value === '' || isNaN(value)) {
                missingFields.push(field.label);
              }
            } else if (!value || (typeof value === 'string' && value.trim() === '')) {
              missingFields.push(field.label);
            }
          }
        }

        if (missingFields.length > 0) {
          toast.error(`Cannot approve: Required fields are missing: ${missingFields.join(', ')}`);
          return;
        }
      }

      const isIdCardApproval = (
        mode === 'hr'
        && action === 'approve'
        && (documentType === 'id_card_front' || documentType === 'id_card_back')
      );
      if (isIdCardApproval && existingProfileData) {
        const conflicts = detectConflicts(ocrData, existingProfileData);
        const unresolved = Object.keys(conflicts).filter(
          fieldKey => !selectedValues[fieldKey]
        );
        if (unresolved.length > 0) {
          toast.error(
            `Resolve ${unresolved.length} conflicting field(s) before approval.`
          );
          return;
        }
      }

      const endpoints = getApiEndpoints();
      let response;
      if (documentType === 'medical_certificate') {
        const applicationId = (documentData?.application_id) || initialOcrData?.application_id;
        if (!applicationId) {
          throw new Error('Missing application_id for medical certificate approval');
        }
        if (action === 'approve') {
          response = await staffAPI.verifyMcAndApproveLeave({ application_id: applicationId, notes: '' });
        } else {
          response = await staffAPI.rejectMcAndLeave({ application_id: applicationId, rejection_reason: rejectionReason });
        }
      } else {
        // Persist HR edits to OCR data before verifying for ALL document types
        // Without this, the verify endpoint reads stale raw OCR data from the database
        if (mode === 'hr' && action === 'approve' && endpoints.editOcr) {
          // Use buildAmendedOcrData (not cleanOcrData) so fields the HR user
          // explicitly cleared are persisted as empty and overwrite the stale
          // raw OCR value, instead of being dropped and silently retained.
          const amendedOcrData = buildAmendedOcrData(ocrData, documentType);
          await api.post(endpoints.editOcr, {
            document_approval_id: documentApprovalId,
            amended_ocr_data: amendedOcrData
          });
        }

        // Prepare payload with selected values for conflict resolution (ID cards only)
        const isIdCard = documentType === 'id_card_front' || documentType === 'id_card_back';
        const payload = {
          document_approval_id: documentApprovalId,
          action: action
        };
        
        if (action === 'reject' && rejectionReason.trim()) {
          payload.rejection_reason = rejectionReason.trim();
          payload.notes = rejectionReason.trim();
        }

        // Include selected values for conflict resolution (HR mode, ID cards only)
        if (isIdCard && mode === 'hr' && Object.keys(selectedValues).length > 0) {
          payload.selected_values = selectedValues;
        }
        
        response = await api.post(endpoints.verify, payload);
      }

      // Show consumption split info when sick leave was partially covered
      if (response.data?.consumption_split?.items?.length > 1) {
        const items = response.data.consumption_split.items;
        const parts = items.map(i => `${i.days} day(s) ${i.leave_type}`).join(', ');
        toast.success(`MC approved — balance split: ${parts}`, { duration: 6000 });
      }
      
      if (onApprovalComplete) {
        onApprovalComplete(response.data);
      }
      
      wasFinalisedRef.current = true;
      onClose();
    } catch (error) {
      console.error('Error processing approval:', error);
      if (error?.response) {
        console.error('Approval error response:', error.response.data);
        toast.error(error.response.data?.error || `Failed to ${action} document`);
      } else {
        toast.error(`Failed to ${action} document`);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Interim Birth Notification handlers ──────────────────────────────────

  const computeInterimDeadline = (dobStr) => {
    if (!dobStr) return null;
    try {
      const dob = new Date(dobStr);
      if (isNaN(dob)) return null;
      const deadline = new Date(dob);
      deadline.setDate(deadline.getDate() + 42);
      return deadline;
    } catch { return null; }
  };

  const handleInterimAction = async (action) => {
    if (action === 'approve') {
      const { spouse_name, child_date_of_birth, hospital, hospital_other, hospital_other_mode,
              child_citizenship_at_birth, source_document_type, source_document_type_other, hr_notes } = interimForm;

      if (!child_date_of_birth) { toast.error("Child's date of birth is required."); return; }
      if (!hospital_other_mode && !hospital) { toast.error('Hospital is required.'); return; }
      if (hospital_other_mode && !hospital_other.trim()) { toast.error('Please specify the hospital name.'); return; }
      if (!child_citizenship_at_birth) { toast.error("Child's citizenship at birth is required."); return; }
      if (!source_document_type) { toast.error('Source document type is required.'); return; }
      if (source_document_type === 'other' && !source_document_type_other.trim()) {
        toast.error('Please describe the source document.'); return;
      }
      if (!hr_notes.trim()) { toast.error('HR Notes is required for approval.'); return; }
    }

    setInterimSaving(true);
    try {
      const payload = {
        action,
        document_approval_id: documentApprovalId,
        ...(action === 'approve' ? {
          spouse_name: interimForm.spouse_name,
          child_date_of_birth: interimForm.child_date_of_birth,
          hospital: interimForm.hospital_other_mode ? interimForm.hospital_other : interimForm.hospital,
          child_citizenship_at_birth: interimForm.child_citizenship_at_birth,
          // Use canonical backend field names
          interim_source_document_type: interimForm.source_document_type,
          interim_source_document_type_other: interimForm.source_document_type === 'other' ? interimForm.source_document_type_other : '',
          notes: interimForm.hr_notes,
        } : {
          rejection_reason: rejectionReason,
        }),
      };
      const response = await api.post('/interim-birth-notification/verify/', payload);
      wasFinalisedRef.current = true;
      if (onApprovalComplete) {
        onApprovalComplete({ success: true, action, message: response.data?.message || `Interim birth notification ${action}d.` });
      }
      toast.success(action === 'approve' ? 'Interim birth notification approved.' : 'Document rejected.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${action} document.`);
    } finally {
      setInterimSaving(false);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-800 bg-gray-100';
    const statusLower = status.toString().toLowerCase();
    switch (statusLower) {
      case 'verified': return 'text-green-800 bg-green-100';
      case 'approved': return 'text-green-800 bg-green-100';
      case 'rejected': return 'text-red-800 bg-red-100';
      case 'pending': return 'text-blue-800 bg-blue-100';
      case 'ocr_completed': return 'text-blue-800 bg-blue-100';
      case 'submitted': return 'text-purple-800 bg-purple-100';
      case 'processing': return 'text-yellow-800 bg-yellow-100';
      case 'processing_ocr': return 'text-yellow-800 bg-yellow-100';
      default: return 'text-gray-800 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return AlertCircle;
    const statusLower = status.toString().toLowerCase();
    switch (statusLower) {
      case 'verified': return CheckCircle;
      case 'approved': return CheckCircle;
      case 'rejected': return XCircle;
      case 'pending': return Clock;
      case 'ocr_completed': return Eye;
      case 'submitted': return FileText;
      case 'processing': return Clock;
      case 'processing_ocr': return Clock;
      default: return AlertCircle;
    }
  };

  const getStatusDisplayText = (status) => {
    if (!status) return 'Unknown';
    const statusLower = status.toString().toLowerCase();
    switch (statusLower) {
      case 'verified': return 'Verified';
      case 'approved': return 'Verified';
      case 'rejected': return 'Rejected';
      case 'pending': return 'Pending Review';
      case 'ocr_completed': return 'Pending Review';
      case 'submitted': return 'Pending HR';
      case 'processing': return 'Processing';
      case 'processing_ocr': return 'Processing OCR';
      default: return 'Unknown';
    }
  };

  // OCR data is always visible - no masking needed
  
  if (!isOpen) return null;

  // Determine if the document should be read-only (verified or explicit view mode)
  const resolvedStatus = (documentData?.document_info?.status || documentData?.status || documentData?.approval_status || documentData?.verification_status || documentData?.document_status || documentData?.current_status || '').toString().toLowerCase();
  const isVerifiedStatus = ['verified', 'approved', 'validated'].includes(resolvedStatus);
  const isSubmittedStatus = ['submitted'].includes(resolvedStatus);
  const isStuckOcrStatus = ['processing', 'processing_ocr'].includes(resolvedStatus);
  // Employees cannot edit once submitted; HR can still edit when submitted.
  // processing_ocr is an in-flight flag, not a finished document — HR must
  // still be able to type from the scan or re-analyse after a failed extract.
  const isReadOnly = (
    (mode !== 'hr' && (viewMode === 'view' || isVerifiedStatus || isSubmittedStatus)) ||
    (mode === 'hr' && isVerifiedStatus) ||
    (mode === 'hr' && viewMode === 'view' && !isStuckOcrStatus)
  );

  const extractedFieldKeys = OCR_EXTRACT_AGAIN_TYPES.includes(documentType)
    ? getFieldConfig().map((field) => field.key)
    : [];
  const hasExtractedOcr = hasMeaningfulDocumentOcrData(ocrData, extractedFieldKeys);
  const canExtractAgain = (
    OCR_EXTRACT_AGAIN_TYPES.includes(documentType)
    && !isReadOnly
    && workflowStep === 'review'
    && !loading
    && (
      documentType === 'medical_certificate'
        ? Boolean(applicationIdProp || documentData?.application_id)
        : Boolean(documentApprovalId)
    )
  );
  const showEmptyOcrRecovery = canExtractAgain && !hasExtractedOcr;

  const extractAgainButton = (canExtractAgain && hasExtractedOcr) ? (
    <ExtractAgainButton onClick={handleRerunOcr} busy={rerunningOcr} />
  ) : null;

  // Derive employee display name for header subtitle (props → fetched data → profile)
  const headerEmployeeName = employeeNameProp
    || documentData?.employee_data?.full_name
    || employeeProfile?.full_name || employeeProfile?.name
    || null;
  const headerStaffCode = employeeStaffCodeProp
    || documentData?.employee_data?.staff_code || documentData?.employee_data?.staff_id
    || employeeProfile?.staff_id
    || null;

  // Title is derived from a single label map so every document type — current
  // and future — renders the correct header in both modes. Unknown types fall
  // back to a safe generic label rather than a wrong specific one.
  const documentTypeLabel = DOCUMENT_TYPE_LABELS[documentType] || 'Document';
  const headerTitle = mode === 'hr'
    ? `${documentTypeLabel} Verification`
    : `Review ${documentTypeLabel}`;

  const headerStatus = documentData?.document_info?.status
    || documentData?.status
    || documentData?.approval_status
    || documentData?.verification_status
    || documentData?.document_status
    || documentData?.current_status
    || '';
  const headerStatusLower = headerStatus.toString().toLowerCase();
  const headerStatusLabel = headerStatusLower === 'approved'
    ? 'Verified'
    : ['pending', 'ocr_completed', 'processing', 'processing_ocr'].includes(headerStatusLower)
      ? 'Pending Review'
      : undefined;

  // Subtitle is human context only. Employees never see storage filenames
  // (`prof_cert_S0102_…`) — that is internal noise. HR sees the employee;
  // omit the line entirely when there is nothing useful to show.
  const headerSubtitle = headerEmployeeName
    ? (
      <>
        {headerEmployeeName}
        {headerStaffCode && (
          <>
            <span className="mx-1.5 text-gray-400">·</span>
            <span>{headerStaffCode}</span>
          </>
        )}
      </>
    )
    : null;

  const closeButtonClass = resolvedTheme === 'admin' ? 'modal-close-admin' : 'modal-close';

  // DS modal-header pattern: title + status (+ optional context chips) on the
  // left; close alone on the right. No decorative document-type icon — the
  // title already names the document, and a large icon forces 2-line wraps
  // on mobile next to StatusBadge + close.
  const docVerifyHeader = (
    <div className="modal-header">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <h3 className="modal-title truncate min-w-0">{headerTitle}</h3>
          {(documentType === 'id_card_front' || documentType === 'id_card_back') && (
            <InfoBadge
              color={documentType === 'id_card_front' ? 'blue' : 'green'}
              label={documentType === 'id_card_front' ? 'Front' : 'Back'}
              className="flex-shrink-0"
            />
          )}
          {documentType === 'marriage_certificate' && workflowStep === 'submitted' && (
            <InfoBadge color="purple" label="Submitted for Review" className="flex-shrink-0" />
          )}
          {detectedScheme && <BcaSchemeBadge scheme={detectedScheme} />}
          {headerStatus ? (
            <div className="flex-shrink-0">
              <StatusBadge status={headerStatus} label={headerStatusLabel} showIcon />
            </div>
          ) : null}
        </div>
        {headerSubtitle && (
          <p className="text-sm text-gray-500 mt-0.5 truncate">
            {headerSubtitle}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleClose}
        className={closeButtonClass}
        title="Close"
        aria-label="Close modal"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  return (
    <>
    <Modal
      isOpen={true}
      onClose={handleClose}
      title="Document Verification"
      size="xl"
      theme={resolvedTheme}
      customHeader={docVerifyHeader}
      className={`!max-w-7xl ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'max-h-[90vh]'}`}
      contentClassName="!p-0 !overflow-hidden flex flex-col"
    >



        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className={`w-8 h-8 border-4 ${resolvedTheme === 'admin' ? 'border-red-600' : 'border-blue-600'} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
              <p className="text-gray-600">Loading document data...</p>
            </div>
          </div>
        ) : (
          <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>
            {/* Mobile tab bar */}
            {isMobile && (
              <MobileVerificationTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                documentLabel="Document"
                formLabel="Verification"
                isAdmin={mode === 'hr'}
              />
            )}
            {/* Left Panel - Document Viewer */}
            <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-2/3'} ${!isMobile ? 'border-r' : ''} bg-gray-50 flex flex-col min-h-0`}>
              {/* Desktop only — on mobile the Document | Verification tabs already name the surface */}
              {!isMobile && (
                <VerificationPanelHeader title="Document" />
              )}
            <div className="flex-1 p-4 overflow-hidden">
                <div className="relative w-full h-full" style={isMobile ? undefined : { minHeight: '600px' }}>
                  {loadingDocument ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <div className={`w-8 h-8 border-4 ${resolvedTheme === 'admin' ? 'border-red-600' : 'border-blue-500'} border-t-transparent rounded-full animate-spin mx-auto mb-4`}></div>
                        <p>Loading document...</p>
                      </div>
                    </div>
                  ) : documentBlobUrl ? (
                  // Interactive viewer (zoom/pan) for images; PdfViewer for PDFs (Android-safe);
                  // iframe only as a last-resort fallback for other types.
                  (getPreviewDocumentType(documentData?.document_info?.filename) === 'image') ? (
                    <div
                      ref={viewer.containerRef}
                      {...viewer.panHandlers}
                      className="relative w-full h-full overflow-hidden bg-white border rounded-lg flex items-center justify-center"
                      style={viewer.containerStyle}
                    >
                      <img
                        src={documentBlobUrl}
                        alt="Document Preview"
                        className="select-none pointer-events-none"
                        draggable={false}
                        style={{
                          transform: viewer.imageTransform,
                          transformOrigin: 'center center',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                      />
                      <div
                        className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={viewer.zoomOut}
                          className={resolvedTheme === 'admin' ? 'icon-button-admin' : 'icon-button'}
                          title="Zoom out"
                          aria-label="Zoom out"
                        >
                          <ZoomOut className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={viewer.zoomIn}
                          className={resolvedTheme === 'admin' ? 'icon-button-admin' : 'icon-button'}
                          title="Zoom in"
                          aria-label="Zoom in"
                        >
                          <ZoomIn className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={viewer.rotate}
                          className={resolvedTheme === 'admin' ? 'icon-button-admin' : 'icon-button'}
                          title="Rotate clockwise"
                          aria-label="Rotate clockwise"
                        >
                          <RotateCw className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={viewer.reset}
                          className={resolvedTheme === 'admin' ? 'icon-button-admin' : 'icon-button'}
                          title="Fit to panel"
                          aria-label="Fit to panel"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <span className="w-px h-6 bg-gray-200 mx-0.5" aria-hidden="true" />
                        <button
                          type="button"
                          onClick={handleDownloadDocument}
                          className={resolvedTheme === 'admin' ? 'icon-button-admin' : 'icon-button'}
                          title="Download original"
                          aria-label="Download original document"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (getPreviewDocumentType(documentData?.document_info?.filename) === 'pdf') ? (
                    <div className="w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? undefined : { minHeight: '600px' }}>
                      <PdfViewer
                        src={documentBlobUrl}
                        title="Document Preview"
                        fileName={documentData?.document_info?.filename || 'document.pdf'}
                        onDownload={handleDownloadDocument}
                        theme={resolvedTheme}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? undefined : { minHeight: '600px' }}>
                      <iframe
                        key={documentBlobUrl}
                        src={documentBlobUrl}
                        className="w-full h-full border-0"
                        title="Document Preview"
                        style={isMobile ? { height: '100%', width: '100%', display: 'block' } : { minHeight: '600px', height: '100%', width: '100%', display: 'block' }}
                        allow="fullscreen"
                      />
                    </div>
                  )
                  ) : documentError ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                        <p className="text-red-600 font-medium mb-2">Failed to load document</p>
                        <p className="text-sm text-gray-500 mb-4">{documentError}</p>
                        {documentData?.document_info?.file_url && (
                          <button
                            onClick={() => {
                              setDocumentError(null);
                              currentFileUrlRef.current = null; // Reset to allow retry
                              loadDocumentBlob(documentData.document_info.file_url);
                            }}
                            className="btn btn-sm btn-outline"
                          >
                            Retry Loading
                          </button>
                        )}
                      </div>
                    </div>
                  ) : documentData?.document_info?.file_url ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                        <p className="mb-2">Document preview not loaded</p>
                        <p className="text-xs text-gray-400 mb-4">
                          File: {documentData.document_info.filename}
                        </p>
                        <button
                          onClick={() => {
                            currentFileUrlRef.current = null; // Reset to allow retry
                            loadDocumentBlob(documentData.document_info.file_url);
                          }}
                          className="btn btn-sm btn-outline mt-2"
                        >
                          Load Document
                        </button>
                        {/* Debug info in development */}
                        {process.env.NODE_ENV === 'development' && (
                          <div className="mt-4 p-2 bg-gray-100 rounded text-xs text-left">
                            <p><strong>Debug Info:</strong></p>
                            <p>Blob URL: {documentBlobUrl ? 'Set' : 'Not set'}</p>
                            <p>Loading: {loadingDocument ? 'Yes' : 'No'}</p>
                            <p>File URL: {documentData.document_info.file_url}</p>
                            <p>Current Ref: {currentFileUrlRef.current || 'null'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>Document preview not available</p>
                        {!documentData && (
                          <p className="text-xs text-gray-400 mt-2">No document data loaded</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── INTERIM BIRTH NOTIFICATION — Right Panel ─────────────── */}
            {documentType === 'interim_birth_notification' && (
              <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>
                {/* Desktop only — mobile tabs already label the Verification surface */}
                {!isMobile && (
                  <VerificationPanelHeader
                    title="Birth Details"
                    trailing={<InfoBadge color="amber" label="Manual Entry" />}
                  />
                )}

                <div className="flex-1 overflow-y-auto overscroll-y-contain p-4 space-y-4" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>

                  {/* ── Provisional leave info banner ── */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700 leading-relaxed">
                        Approval records this as an interim birth notification and may
                        <strong> provisionally credit</strong> Paternity Leave &amp; SPL, subject to
                        citizenship at birth.
                      </p>
                    </div>
                  </div>

                  {/* ── Source document type ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Source Document Type <span className="text-red-500">*</span>
                    </label>
                    <FormSelect
                      value={interimForm.source_document_type}
                      onChange={(val) => setInterimForm(prev => ({ ...prev, source_document_type: val }))}
                      options={[
                        { value: '', label: 'Select document type…' },
                        ...INTERIM_SOURCE_DOC_OPTIONS
                      ]}
                      theme={resolvedTheme}
                      disabled={isReadOnly}
                    />
                    {interimForm.source_document_type === 'other' && (
                      <input
                        type="text"
                        placeholder="Describe document…"
                        value={interimForm.source_document_type_other}
                        onChange={e => setInterimForm(prev => ({ ...prev, source_document_type_other: e.target.value }))}
                        className="input-admin w-full text-sm mt-2"
                        disabled={isReadOnly}
                      />
                    )}
                  </div>

                  {/* ── Spouse / partner name ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Spouse / Partner Name
                      <span className="ml-1 text-gray-400 font-normal">(pre-filled from marriage cert)</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none" />
                      <input
                        type="text"
                        value={interimForm.spouse_name}
                        onChange={e => setInterimForm(prev => ({ ...prev, spouse_name: e.target.value }))}
                        className="input-admin pl-9 w-full text-sm"
                        placeholder="As per marriage certificate"
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>

                  {/* ── Child's date of birth ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Child's Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <DatePickerField
                      theme={resolvedTheme}
                      selected={parseLocalDate(interimForm.child_date_of_birth)}
                      maxDate={new Date()}
                      onChange={(date) => setInterimForm(prev => ({ ...prev, child_date_of_birth: date ? toLocalISODate(date) : '' }))}
                      placeholderText="Select date of birth"
                      disabled={isReadOnly}
                    />
                    {/* Deadline banner — appears once DOB is entered */}
                    {interimForm.child_date_of_birth && (() => {
                      const dl = computeInterimDeadline(interimForm.child_date_of_birth);
                      if (!dl) return null;
                      const today = new Date(); today.setHours(0,0,0,0);
                      const daysLeft = Math.ceil((dl - today) / 86400000);
                      const deadlineStr = formatDate(dl);
                      const isOverdue = daysLeft < 0;
                      const isUrgent = daysLeft >= 0 && daysLeft <= 7;
                      return (
                        <div className={`mt-2 rounded-lg px-3 py-2 flex items-center gap-2 border ${
                          isOverdue ? 'bg-red-50 border-red-300' : isUrgent ? 'bg-amber-50 border-amber-300' : 'bg-indigo-50 border-indigo-200'
                        }`}>
                          <Clock className={`w-4 h-4 flex-shrink-0 ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-indigo-600'}`} />
                          <div>
                            <p className={`text-xs font-semibold ${isOverdue ? 'text-red-700' : isUrgent ? 'text-amber-700' : 'text-indigo-700'}`}>
                              Birth Cert Deadline: {deadlineStr}
                            </p>
                            <p className={`text-[10px] mt-0.5 ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-indigo-600'}`}>
                              {isOverdue ? `${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} overdue` : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* ── Hospital ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Hospital <span className="text-red-500">*</span>
                    </label>
                    {!interimForm.hospital_other_mode ? (
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none z-10" />
                        <FormSelect
                          value={interimForm.hospital}
                          onChange={(val) => {
                            if (val === '__other__') {
                              setInterimForm(prev => ({ ...prev, hospital: '', hospital_other_mode: true }));
                            } else {
                              setInterimForm(prev => ({ ...prev, hospital: val }));
                            }
                          }}
                          options={[
                            { value: '', label: 'Select hospital…' },
                            ...SINGAPORE_HOSPITALS.map(h => ({ value: h, label: h })),
                            { value: '__other__', label: 'Other…' }
                          ]}
                          theme={resolvedTheme}
                          disabled={isReadOnly}
                        />
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500 pointer-events-none z-10" />
                          <input
                            type="text"
                            placeholder="Enter hospital name"
                            value={interimForm.hospital_other}
                            onChange={e => setInterimForm(prev => ({ ...prev, hospital_other: e.target.value }))}
                            className="input-admin pl-9 w-full text-sm"
                            disabled={isReadOnly}
                            autoFocus={!isMobile}
                          />
                        </div>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => setInterimForm(prev => ({ ...prev, hospital_other_mode: false, hospital_other: '' }))}
                            className="text-xs text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                          >
                            ← Back to list
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ── Child citizenship at birth ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Singapore Citizen at Birth? <span className="text-red-500">*</span>
                    </label>
                    {/* DS pill segmented control — admin theme (red active pill) */}
                    <div className="flex bg-gray-100 rounded-full p-1">
                      {[{ val: 'yes', label: 'Yes' }, { val: 'no', label: 'No' }].map(({ val, label }) => (
                        <button
                          key={val}
                          type="button"
                          disabled={isReadOnly}
                          onClick={() => setInterimForm(prev => ({ ...prev, child_citizenship_at_birth: val }))}
                          className={`flex-1 py-1 px-2 text-xs font-medium rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 ${
                            interimForm.child_citizenship_at_birth === val
                              ? 'bg-red-600 text-white shadow-sm'
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {interimForm.child_citizenship_at_birth === 'no' && (
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">
                          No leave will be credited. The employee may resubmit within 12 months if citizenship is subsequently obtained.
                        </p>
                      </div>
                    )}
                    {interimForm.child_citizenship_at_birth === 'yes' && (
                      <div className="mt-2 bg-green-50 border border-green-200 rounded-lg p-2.5 flex gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-green-700">
                          Paternity Leave &amp; SPL will be provisionally credited upon approval.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ── HR notes (required) ── */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      HR Notes <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={interimForm.hr_notes}
                      onChange={e => setInterimForm(prev => ({ ...prev, hr_notes: e.target.value }))}
                      className={`textarea-admin w-full text-sm ${
                        !interimForm.hr_notes.trim() && !isReadOnly ? 'border-red-300 focus:border-red-500' : ''
                      }`}
                      rows={3}
                      placeholder="Required — document source details, verification notes, or remarks for audit trail…"
                      disabled={isReadOnly}
                    />
                    {!interimForm.hr_notes.trim() && !isReadOnly && (
                      <p className="mt-1 text-[10px] text-red-500">HR Notes is required before approving.</p>
                    )}
                  </div>
                </div>

                {/* ── Action buttons ── */}
                {!isReadOnly && (
                  <div className="p-4 border-t bg-white flex-shrink-0 space-y-2">
                    {showRejectForm ? (
                      <div className="space-y-2">
                        <textarea
                          value={rejectionReason}
                          onChange={e => setRejectionReason(e.target.value)}
                          placeholder="Reason for rejection (required)…"
                          className="textarea-admin w-full text-sm"
                          rows={3}
                        />
                        <div className="flex gap-2 btn-row-spread">
                          <button
                            type="button"
                            onClick={() => setShowRejectForm(false)}
                            className="btn btn-admin-outline"
                            disabled={interimSaving}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleInterimAction('reject')}
                            className="btn btn-danger"
                            disabled={interimSaving || !rejectionReason.trim()}
                          >
                            {interimSaving ? 'Rejecting…' : 'Confirm Reject'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2 btn-row-spread">
                        <button
                          type="button"
                          onClick={() => setShowRejectForm(true)}
                          className="btn btn-admin-outline"
                          disabled={interimSaving}
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInterimAction('approve')}
                          className="btn btn-success"
                          disabled={interimSaving}
                        >
                          {interimSaving ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Approving…
                            </span>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Right Panel - OCR Data Form */}
            {documentType !== 'interim_birth_notification' && (
            <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>
              {/* Desktop only — mobile tabs already label the Verification surface */}
              {!isMobile && (
                <VerificationPanelHeader
                  title="Extracted Information"
                  trailing={extractAgainButton}
                />
              )}

              <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 space-y-4" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                {isMobile && extractAgainButton ? (
                  <div className="flex justify-end">{extractAgainButton}</div>
                ) : null}
                {/* OCR Data Status messages removed - form fields show data directly */}

                {showEmptyOcrRecovery && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <span className="font-semibold text-amber-800 text-sm">No extracted data found</span>
                    </div>
                    <p className="text-xs text-amber-700 mb-3">
                      We couldn&apos;t read the details from this scan. Fill in the fields from the document, or extract them again.
                    </p>
                    <ExtractAgainButton onClick={handleRerunOcr} busy={rerunningOcr} />
                  </div>
                )}

                {/* Conflict Resolution Banner (HR mode only) */}
                {(() => {
                  // Debug logging
                  console.log('🔍 Conflict Detection Debug:', {
                    mode,
                    hasExistingProfileData: !!existingProfileData,
                    existingProfileData,
                    ocrDataKeys: Object.keys(ocrData),
                    ocrDataSample: Object.keys(ocrData).slice(0, 5).reduce((acc, key) => {
                      acc[key] = ocrData[key];
                      return acc;
                    }, {})
                  });
                  
                  const conflicts = mode === 'hr' && existingProfileData ? detectConflicts(ocrData, existingProfileData) : {};
                  const hasConflicts = Object.keys(conflicts).length > 0;
                  
                  console.log('🔍 Conflicts detected:', {
                    hasConflicts,
                    conflictCount: Object.keys(conflicts).length,
                    conflicts
                  });
                  
                  return hasConflicts && mode === 'hr' ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        <span className="font-semibold text-yellow-800">
                          {Object.keys(conflicts).length} Conflict(s) Detected
                        </span>
                      </div>
                      <p className="text-sm text-yellow-700">
                        Please select which value to use for each conflicting field below.
                      </p>
                    </div>
                  ) : null;
                })()}

                {/* Bank Statement - Account Holder Name Cross-Verification Banner */}
                {documentType === 'bank_statement' && ocrData.ocr_account_holder_name && (employeeProfile?.name_as_per_nric_fin || employeeProfile?.full_name) && (() => {
                  const holderName = (ocrData.ocr_account_holder_name || '').toUpperCase().trim();
                  const employeeReferenceName = (
                    employeeProfile.name_as_per_nric_fin
                    || employeeProfile.full_name
                    || ''
                  );
                  const empName = employeeReferenceName.toUpperCase().trim();
                  // Reuse nameMatches logic: exact or partial word overlap
                  const isMatch = holderName === empName || (() => {
                    const words1 = holderName.split(/\s+/).filter(w => w.length > 2);
                    const words2 = empName.split(/\s+/).filter(w => w.length > 2);
                    return words1.some(w1 => words2.some(w2 => w2.includes(w1) || w1.includes(w2)));
                  })();
                  return isMatch ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-green-800 text-sm">Account holder name matches employee record</span>
                          <p className="text-xs text-green-700 mt-0.5">
                            Bank: <span className="font-medium">{ocrData.ocr_account_holder_name}</span> — Legal name: <span className="font-medium">{employeeReferenceName}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-red-800 text-sm">Account holder name does NOT match employee record</span>
                          <p className="text-xs text-red-700 mt-0.5">
                            Bank: <span className="font-medium">{ocrData.ocr_account_holder_name}</span> — Legal name: <span className="font-medium">{employeeReferenceName}</span>
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            Please verify this is the correct bank statement for this employee before proceeding.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Role Selection - Only shown as last resort (employee mode only) */}
                {!loading && mode !== 'hr' && documentType === 'marriage_certificate' && !employeeRole && !detectedRole && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      Unable to Auto-Detect Role
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      We couldn't automatically determine your role from your name or profile. 
                      Please select whether you are the Bridegroom or Bride on this certificate.
                    </p>
                    <div className="inline-flex bg-gray-100 rounded-lg p-1">
                      <button 
                        onClick={() => handleRoleSelection('bridegroom')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          selectedRole === 'bridegroom' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        👨 Bridegroom
                      </button>
                      <button 
                        onClick={() => handleRoleSelection('bride')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          selectedRole === 'bride' 
                            ? 'bg-white text-pink-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        👰 Bride
                      </button>
                    </div>
                  </div>
                )}

                {/* Dynamic Fields based on Document Type */}
                {documentType === 'driving_license' ? renderDrivingLicenseSection() : getFieldConfig()
                  .filter(field => isFieldVisible(field, ocrData))
                  .map((field, index) => {
                  const IconComponent = field.icon;
                  const colors = ['blue', 'green', 'purple', 'yellow'];
                  const sectionColor = colors[index % colors.length];
                  

                  
                  // For marriage certificates, add role badges (employee mode only)
                  const isMarriageField = mode !== 'hr' && documentType === 'marriage_certificate' && 
                    (field.key === 'bridegroom_full_name' || field.key === 'bride_full_name');
                  
                  const isEmployeeField = isMarriageField && (
                    (field.key === 'bridegroom_full_name' && employeeRole === 'bridegroom') ||
                    (field.key === 'bride_full_name' && employeeRole === 'bride')
                  );
                  
                  return (
                    <div key={field.key} className={`bg-${sectionColor}-50 rounded-lg p-4`}>
                      <h4 className={`font-semibold text-${sectionColor}-900 mb-3 flex items-center gap-2`}>
                        <IconComponent className="w-4 h-4" />
                        {field.label}
                        {(field.required || (field.conditionallyRequired && documentType === 'education_certificate' && field.key === 'field_of_study' && isFieldOfStudyRequired(ocrData))) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                        {isMarriageField && (
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                            isEmployeeField 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {isEmployeeField ? 'You' : 'Spouse'}
                          </span>
                        )}
                      </h4>
                      <div className="grid grid-cols-1 gap-4">
                        {(() => {
                          // Check for conflicts (HR mode only)
                          const conflicts = mode === 'hr' && existingProfileData ? detectConflicts(ocrData, existingProfileData) : {};
                          const hasConflict = conflicts[field.key];
                          
                          // Render conflict resolution UI if conflict exists
                          if (hasConflict && mode === 'hr') {
                            const conflict = conflicts[field.key];
                            const isSelected = selectedValues[field.key];
                            
                            return (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <AlertTriangle className="w-3.5 h-3.5 text-yellow-600" />
                                  <span className="text-xs font-medium text-yellow-800">
                                    Conflict Detected - Select which value to use:
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3">
                                  {/* Current Profile Option */}
                                  <button
                                    type="button"
                                    onClick={() => setSelectedValues(prev => ({
                                      ...prev,
                                      [field.key]: 'existing'
                                    }))}
                                    className={`p-2.5 border-2 rounded-lg transition-all text-left ${
                                      isSelected === 'existing'
                                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-gray-700">Current Profile</span>
                                      {isSelected === 'existing' && (
                                        <CheckCircle className="w-4 h-4 text-blue-600" />
                                      )}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900 leading-tight">
                                      {conflict.existing
                                        ? (field.type === 'date' ? formatDate(conflict.existing) : conflict.existing)
                                        : '(empty)'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Currently in database
                                    </div>
                                  </button>
                                  
                                  {/* ID Card (OCR) Option */}
                                  <button
                                    type="button"
                                    onClick={() => setSelectedValues(prev => ({
                                      ...prev,
                                      [field.key]: 'ocr'
                                    }))}
                                    className={`p-2.5 border-2 rounded-lg transition-all text-left ${
                                      isSelected === 'ocr'
                                        ? 'border-green-500 bg-green-50 shadow-sm'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-gray-700">{`${DOCUMENT_TYPE_LABELS[documentType] || 'Document'} (OCR)`}</span>
                                      {isSelected === 'ocr' && (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      )}
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900 leading-tight">
                                      {conflict.ocr
                                        ? (field.type === 'date' ? formatDate(conflict.ocr) : conflict.ocr)
                                        : '(empty)'}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {`From verified ${(DOCUMENT_TYPE_LABELS[documentType] || 'document').toLowerCase()}`}
                                    </div>
                                  </button>
                                </div>
                                
                                <div className="mt-2 text-xs text-gray-600">
                                  💡 Selected value will be saved to profile on verification
                                </div>
                              </div>
                            );
                          }
                          
                          // No conflict - render normal field input
                          return null;
                        })()}
                        
                        <div>
                          {(() => {
                            // Check if field has conflict - if so, show selected value or normal input
                            const conflicts = mode === 'hr' && existingProfileData ? detectConflicts(ocrData, existingProfileData) : {};
                            const hasConflict = conflicts[field.key];
                            const isSelected = selectedValues[field.key];
                            
                            // If conflict exists and 'existing' is selected, show existing value as read-only
                            if (hasConflict && mode === 'hr' && isSelected === 'existing') {
                              const conflict = conflicts[field.key];
                              return (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <div className="text-sm text-gray-600 mb-1">Selected value (will be saved):</div>
                                  <div className="text-lg font-semibold text-gray-900">{field.type === 'date' ? formatDate(conflict.existing) : conflict.existing}</div>
                                  <div className="text-xs text-gray-500 mt-1">✓ Using current profile value</div>
                                </div>
                              );
                            }
                            
                            // Check if field matches existing profile (no conflict, but has existing value)
                            // Reuse conflicts from above (line 1779)
                            const fieldValue = ocrData[field.key];
                            const existingValue = existingProfileData?.[(() => {
                              const fieldMapping = {
                                'id_card_name': 'name_as_per_nric_fin',
                                'id_card_nric_fin_number': 'nric_fin',
                                'id_card_dob': 'dob',
                                'id_card_sex': 'gender',
                                'id_card_nationality': 'nationality',
                                'id_card_race': 'race',
                                'id_card_type': 'employment_pass_type',
                                'id_card_issue_date': 'pass_issue_date',
                                'id_card_wp_number': 'wp_number',
                                'id_card_address': 'residential_address',
                                'id_card_postal_code': 'residential_postal_code',
                                'id_card_residential_unit_number': 'residential_unit_number',
                                'occupation': 'occupation',
                                'date_of_application': 'wp_application_date',
                                'date_of_expiry': 'pass_expiry_date',
                              };
                              return fieldMapping[field.key];
                            })()];
                            const valuesMatch = !hasConflict && fieldValue && existingValue && 
                              String(fieldValue).trim() === String(existingValue).trim();
                            
                            // Normal field input rendering
                            return (
                              <>
                                {field.type === 'select' ? (
                            <div className="relative">
                              {/* Special handling for citizenship status with segmented buttons */}
                              {field.key === 'child_citizenship_status' ? (
                                <>
                                  <div className="flex bg-gray-100 rounded-full px-1 py-0.5 shadow-inner">
                                    {(() => {
                                      const rawValue = ocrData[field.key] ?? '';
                                      const currentValue = rawValue.toString().trim().toLowerCase();
                                      
                                      // Debug logging
                                      console.log('🔍 Citizenship Debug:', {
                                        fieldKey: field.key,
                                        rawValue,
                                        currentValue,
                                        isYes: currentValue === 'yes',
                                        isNo: currentValue === 'no',
                                        allOcrData: ocrData
                                      });
                                      
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => handleOcrDataChange(field.key, 'yes')}
                                            disabled={isReadOnly}
                                            className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                              currentValue === 'yes'
                                                ? 'bg-green-500 text-white shadow-md'
                                                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                            } ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                          >
                                            <div className="flex items-center justify-center gap-2">
                                              <CheckCircle className="w-4 h-4" />
                                              Yes
                                            </div>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleOcrDataChange(field.key, 'no')}
                                            disabled={isReadOnly}
                                            className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                              currentValue === 'no'
                                                ? 'bg-red-500 text-white shadow-md'
                                                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                                            } ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                          >
                                            <div className="flex items-center justify-center gap-2">
                                              <XCircle className="w-4 h-4" />
                                              No
                                            </div>
                                          </button>
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <p className="mt-2 text-xs text-gray-500 text-center">
                                    Select whether the child is a Singapore citizen for leave eligibility
                                  </p>
                                </>
                              ) : (
                                <>
                                  {/* Debug logging for other select fields */}
                                  {field.key === 'child_citizenship_status' && console.log('🔍 Citizenship Debug:', {
                                    fieldKey: field.key,
                                    ocrDataValue: ocrData[field.key],
                                    ocrDataType: typeof ocrData[field.key],
                                    fieldOptions: field.options,
                                    allOcrData: ocrData
                                  })}
                                  <FormSelect
                                    value={ocrData[field.key] || ''}
                                    onChange={(val) => handleOcrDataChange(field.key, val)}
                                    disabled={isReadOnly}
                                    options={[
                                      { value: '', label: `Select ${field.label.toLowerCase().replace('?', '')}` },
                                      ...(field.options
                                        ? field.options.map(option => {
                                            const normalized = normalizeOption(option);
                                            return { value: normalized.value, label: normalized.label };
                                          })
                                        : [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]
                                      )
                                    ]}
                                    theme={resolvedTheme}
                                  />
                                </>
                              )}
                            </div>
                                                      ) : field.key === 'ocr_bank_name' ? (
                              /* Bank Name dropdown with "Other" free-text fallback */
                              (() => {
                                const currentVal = ocrData[field.key] || '';
                                const isInList = singaporeBankNames.includes(currentVal);
                                const showOtherInput = bankNameOtherMode || (currentVal !== '' && !isInList);
                                const bankMeta = singaporeBanksCatalog.find(
                                  (b) => b.legal_name === currentVal
                                );
                                const hintParts = [];
                                if (bankMeta?.mas_bank_code) {
                                  hintParts.push(`Bank code ${bankMeta.mas_bank_code}`);
                                }
                                if (bankMeta?.swift_bic) {
                                  hintParts.push(`BIC ${bankMeta.swift_bic}`);
                                }
                                return (
                                  <div className="space-y-2">
                                    <FormSelect
                                      value={isInList ? currentVal : (showOtherInput ? '__OTHER__' : '')}
                                      onChange={(val) => {
                                        if (val === '__OTHER__') {
                                          setBankNameOtherMode(true);
                                          if (isInList) handleOcrDataChange(field.key, '');
                                        } else {
                                          setBankNameOtherMode(false);
                                          handleOcrDataChange(field.key, val);
                                        }
                                      }}
                                      options={[
                                        { value: '', label: 'Select bank...' },
                                        ...singaporeBankNames.map(bank => ({ value: bank, label: bank })),
                                        { value: '__OTHER__', label: 'Other' }
                                      ]}
                                      theme={resolvedTheme}
                                      disabled={isReadOnly}
                                    />
                                    {isInList && hintParts.length > 0 && (
                                      <p className="text-xs text-gray-500">
                                        {hintParts.join(' · ')}
                                      </p>
                                    )}
                                    {/* Show text input when "Other" is selected or OCR value not in list */}
                                    {showOtherInput && (
                                      <input
                                        type="text"
                                        value={currentVal}
                                        onChange={(e) => handleOcrDataChange(field.key, e.target.value)}
                                        className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                        placeholder="Enter bank name"
                                        disabled={isReadOnly}
                                        readOnly={isReadOnly}
                                      />
                                    )}
                                  </div>
                                );
                              })()
                          ) : field.type === 'combobox' ? (
                              <DatalistCombobox
                                value={ocrData[field.key] || ''}
                                onChange={(val) => handleOcrDataChange(field.key, val)}
                                suggestions={field.suggestions || []}
                                placeholder={`Select or type ${field.label.toLowerCase()}`}
                                disabled={isReadOnly}
                                className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              />
                          ) : field.type === 'date' ? (
                              <div>
                                <DatePickerField
                                  theme={resolvedTheme}
                                  selected={parseLocalDate(ocrData[field.key])}
                                  onChange={(date) => handleOcrDataChange(field.key, date ? toLocalISODate(date) : '')}
                                  placeholderText={`Select ${field.label.toLowerCase()}`}
                                  disabled={isReadOnly}
                                  isClearable={!field.required}
                                />
                                {field.key === 'ocr_passport_expiry_date' && !isReadOnly && (() => {
                                  const expiry = parseLocalDate(ocrData[field.key]);
                                  if (!expiry) return null;
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  if (expiry >= today) return null;
                                  return (
                                    <p className="mt-1 text-xs text-amber-700">
                                      This date is in the past. Check the expiry on the document — extraction sometimes reads the date of issue.
                                    </p>
                                  );
                                })()}
                              </div>
                          ) : (
                            <input
                              type={field.type || 'text'}
                              value={field.type === 'date' ? formatDate(ocrData[field.key]) : (ocrData[field.key] || '')}
                              onChange={(e) => handleOcrDataChange(field.key, e.target.value)}
                              className={`input-admin ${isReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                              disabled={isReadOnly}
                              readOnly={isReadOnly}
                            />
                          )}
                                
                          {/* Show match indicator for non-conflicting fields (HR mode only) */}
                          {(() => {
                            const conflicts = mode === 'hr' && existingProfileData ? detectConflicts(ocrData, existingProfileData) : {};
                            const hasConflict = conflicts[field.key];
                            const fieldValue = ocrData[field.key];
                            const fieldMapping = {
                              'id_card_name': 'name_as_per_nric_fin',
                              'id_card_nric_fin_number': 'nric_fin',
                              'id_card_dob': 'dob',
                              'id_card_sex': 'gender',
                              'id_card_nationality': 'nationality',
                              'id_card_race': 'race',
                              'id_card_type': 'employment_pass_type',
                              'id_card_issue_date': 'pass_issue_date',
                              'id_card_wp_number': 'wp_number',
                              'id_card_address': 'residential_address',
                              'id_card_postal_code': 'residential_postal_code',
                              'occupation': 'occupation',
                              'date_of_application': 'wp_application_date',
                              'date_of_expiry': 'pass_expiry_date',
                            };
                            const profileField = fieldMapping[field.key];
                            const existingValue = existingProfileData?.[profileField];
                            const valuesMatch = !hasConflict && fieldValue && existingValue && 
                              String(fieldValue).trim() === String(existingValue).trim();
                            
                            return valuesMatch && mode === 'hr' ? (
                              <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                                <CheckCircle className="w-4 h-4" />
                                <span>Matches current profile</span>
                              </div>
                            ) : null;
                          })()}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* OCR Confidence Score removed - not needed for document verification */}

                {/* Eligibility Preview removed - not needed for document verification */}

              </div>

              {/* Sticky Footer with Action Buttons */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0">
                {/* Employee Action Buttons */}
                {mode !== 'hr' && (
                  <div>
                    {isReadOnly ? (
                      <button
                        onClick={onClose}
                        className="btn btn-outline w-full"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Close
                      </button>
                    ) : documentType === 'marriage_certificate' && employeeRole ? (
                      // Marriage certificate with role: show appropriate buttons based on state
                      workflowStep === 'submitted' ? (
                        <button
                          onClick={onClose}
                          className="btn btn-outline w-full"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Close
                        </button>
                      ) : (
                        // Review state: show Cancel and Submit options
                        <div className="flex gap-3 btn-row-spread">
                          <button
                            onClick={handleClose}
                            disabled={saving || isDiscarding}
                            className="btn btn-outline"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </button>
                          <button
                            onClick={handleSubmitForReview}
                            disabled={saving || isDiscarding}
                            className="btn btn-primary"
                          >
                            {saving ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            ) : (
                              <Check className="w-4 h-4 mr-2" />
                            )}
                            Submit
                          </button>
                        </div>
                      )
                    ) : (
                      // Default editable: Cancel discards unsubmitted draft (same path as X / backdrop)
                      <div className="flex gap-3 btn-row-spread">
                        <button
                          onClick={handleClose}
                          disabled={saving || isDiscarding}
                          className="btn btn-outline"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveOcrData}
                          disabled={saving || isDiscarding}
                          className="btn btn-primary"
                        >
                          {saving ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Submit
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons for HR Mode */}
                {mode === 'hr' && HR_REVIEWABLE_STATUSES.includes(resolvedStatus) && (
                  showRejectForm ? (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Reason for rejection
                      </label>
                      <textarea
                        rows={3}
                        className="textarea-admin text-sm"
                        placeholder="Enter reason for rejection..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                      <div className="flex gap-3 btn-row-spread">
                        <button
                          onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                          disabled={saving}
                          className="btn btn-outline"
                        >
                          Back
                        </button>
                        <button
                          onClick={() => handleApprovalAction('reject')}
                          disabled={saving || !rejectionReason.trim()}
                          className="btn btn-danger"
                        >
                          {saving ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          ) : (
                            <X className="w-4 h-4 mr-2" />
                          )}
                          Confirm Reject
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-3 btn-row-spread">
                      <button
                        onClick={() => setShowRejectForm(true)}
                        disabled={saving}
                        className="btn btn-danger"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprovalAction('approve')}
                        disabled={saving}
                        className="btn btn-success"
                      >
                        {saving ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Verify
                      </button>
                    </div>
                  )
                )}
                {/* Close button for HR Mode when document is already verified/approved/rejected */}
                {mode === 'hr' && !HR_REVIEWABLE_STATUSES.includes(resolvedStatus) && (
                  <button
                    onClick={onClose}
                    className="btn btn-outline w-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Close
                  </button>
                )}
              </div>
            </div>
            )}
          </div>
        )}
    </Modal>
    <ConfirmModal
      isOpen={showCloseConfirm}
      title={closeConfirmMessage.startsWith('You have unsaved') ? 'Discard changes?' : 'Discard uploaded document?'}
      message={closeConfirmMessage}
      confirmLabel="Discard"
      cancelLabel="Keep editing"
      variant="danger"
      onConfirm={handleConfirmClose}
      onCancel={() => setShowCloseConfirm(false)}
      isLoading={isDiscarding}
      theme={resolvedTheme}
    />
    </>
  );
};

export default DocumentVerificationModal;